import React from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiChevronDown,
  FiClock,
  FiDownload,
  FiEdit2,
  FiExternalLink,
  FiInfo,
  FiMoreVertical,
  FiPower,
  FiPlus,
  FiSearch,
  FiSettings,
  FiTrash2,
} from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa6";
import {
  LuCalendar,
  LuLandmark,
  LuList,
  LuRefreshCw,
  LuShield,
  LuUserCog,
  LuUsers,
} from "react-icons/lu";
import { useParams } from "react-router-dom";
import PayoutDateRangeFilter from "./PayoutDateRangeFilter";
import PayoutDataTable from "./PayoutDataTable";
import PayoutDropdown from "./PayoutDropdown";
import PayoutFilterChips from "./PayoutFilterChips";
import PayoutSearchFilter from "./PayoutSearchFilter";
import PayoutTableCell from "./PayoutTableCell";
import { userFromApiRecord } from "./api/payoutApiAdapters";
import {
  approvalGroupFromApiRecord,
  approvalGroupPayload,
  approvalRuleFromApiRecord,
  approvalRulePayload,
  findRuleByRouteKey,
  getRuleDetailsPath,
  getRuleEditPath,
  getRuleRouteKey,
  normalizeApprovalPriority,
  ruleMatrixPayload,
  ruleMatrixRowsFromApiRecord,
  workflowStatsFromSummary,
} from "./api/payoutApprovalRulesAdapters";
import {
  createPayoutIdempotencyKey,
  isPayoutApiMode,
  payoutApi,
} from "./api/payoutApiClient";
import {
  errorMessage,
  isPayoutStepUpEnabled,
  rupeesToPaise,
  verifyStepUp,
} from "./api/payoutMutationHelpers";
import {
  normalizeAmountInput,
  validateOtpCode,
  validatePositiveAmountInput,
} from "./api/payoutValidation";
import { usePayoutResource } from "./api/usePayoutResource";
import {
  filterMemberOptionsForApprovalGroup,
} from "../../../utils/payoutAccess";

const workflowStats = [
  {
    label: "Active Workflows",
    value: "0",
    helper: "-",
    icon: <LuUsers />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Transactions Covered",
    value: "0",
    helper: "-",
    icon: <LuShield />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Total Workflows",
    value: "0",
    helper: "-",
    icon: <LuList />,
    tone: "bg-[#F59E0B1A] text-[#F97316]",
  },
  {
    label: "Users in Workflow",
    value: "0",
    helper: "-",
    icon: <LuUserCog />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
];

type WorkflowOverviewItem = {
  name: string;
  helper: string;
  appliesTo: string;
  type: string;
  stages: string[];
  icon: React.ReactNode;
  tone: string;
};

type ApprovalRule = {
  name: string;
  appliesTo: string;
  type: string;
  condition: string;
  workflow: string[];
  priority: string;
  status: string;
  icon?: React.ReactNode;
  tone?: string;
  workflowId?: string;
  approvalGroupId?: string;
  approvalGroupName?: string;
  stageGroupIds?: { M?: string; C?: string; A?: string; U?: string };
  amountMinPaise?: number;
  amountMaxPaise?: number | null;
  ruleId?: string;
  createdAt?: string;
  createdBy?: string;
  lastUpdated?: string;
  description?: string;
};

type MemberUserOption = { id: string; label: string; accessLevel?: string };

type RuleMatrixRow = {
  amount: string;
  condition: string;
  workflow: string[];
  priority: string;
  status: string;
  workflowId?: string;
  amountMinPaise?: number;
  amountMaxPaise?: number | null;
  ruleId?: string;
  createdAt?: string;
  createdBy?: string;
  lastUpdated?: string;
  description?: string;
};

const workflows: WorkflowOverviewItem[] = [];
const rules: ApprovalRule[] = [];
const matrixRows: RuleMatrixRow[] = [];

type StoredApprovalRule = Omit<ApprovalRule, "icon" | "tone">;

const APPROVAL_RULES_STORAGE_KEY = "clearn_payout_approval_rules";
const RULE_MATRIX_STORAGE_KEY = "clearn_payout_rule_matrix";

const seedApprovalRuleLabels = new Set(
  [
    "High Value Transfer Workflow",
    "Medium Value Transfer Workflow",
    "Low Value Transfer Workflow",
    "Very High Value Transfer Workflow",
    "Dormant Amount Review Workflow",
    "High Value Transfer Rule",
    "Medium Value Transfer Rule",
    "Low Value Transfer Rule",
    "Very High Value Transfer Rule",
    "Dormant Amount Review Rule",
  ].map((label) => label.toLowerCase()),
);

const seedApprovalGroupLabels = new Set(
  [
    "Finance Workflow",
    "Salary Payments",
    "Scholarship Disbursement",
    "Refunds & Adjustments",
    "Low Value Transfers",
    "Emergency Transfers",
    "Audit Panel",
    "Finance Checkers",
    "School Approvers",
    "School Payouts - Standard Group",
    "High Value Approver Group",
  ].map((label) => label.toLowerCase()),
);

const seedRuleMatrixLabels = new Set(
  [
    "Low value payouts",
    "Standard value payouts",
    "Medium value payouts",
    "High value payouts",
    "Very high value payouts",
    "Dormant account review",
  ].map((label) => label.toLowerCase()),
);

const seedLabel = (value?: unknown) => String(value || "").trim().toLowerCase();

const isSeedApprovalRule = (rule: Partial<ApprovalRule>) =>
  seedApprovalRuleLabels.has(seedLabel(rule.name)) ||
  seedRuleMatrixLabels.has(seedLabel(rule.condition));

const isSeedApprovalGroup = (group: Partial<ApprovalGroup>) =>
  seedApprovalGroupLabels.has(seedLabel(group.name));

const isSeedRuleMatrixRow = (row: Partial<RuleMatrixRow>) =>
  seedRuleMatrixLabels.has(seedLabel(row.condition));

const sanitizeApprovalRules = <T extends Partial<ApprovalRule>>(nextRules: T[]) =>
  nextRules.filter((rule) => !isSeedApprovalRule(rule));

const sanitizeApprovalGroups = <T extends Partial<ApprovalGroup>>(nextGroups: T[]) =>
  isPayoutApiMode() ? nextGroups : nextGroups.filter((group) => !isSeedApprovalGroup(group));

const sanitizeRuleMatrixRows = <T extends Partial<RuleMatrixRow>>(nextRows: T[]) =>
  nextRows.filter((row) => !isSeedRuleMatrixRow(row));

const priorityLabels = ["Urgent", "High", "Medium", "Low"];
const workflowPathOptions = [
  "M",
  "M -> C",
  "M -> A",
  "M -> C -> A",
  "M -> C -> A -> U",
];

const formatPayoutDateTime = (date: Date) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
    timeZoneName: "short",
  }).format(date);

const normalizePriority = normalizeApprovalPriority;

const getApprovalGroupDetailsPath = (groupId: string) =>
  `/payouts/approval-rules/approval-groups/${encodeURIComponent(groupId)}`;

const getApprovalGroupEditPath = (groupId: string) =>
  `/payouts/approval-rules/approval-groups/${encodeURIComponent(groupId)}/edit`;

const getRuleMatrixConfigurePath = () =>
  "/payouts/approval-rules/rule-matrix/configure";

const workflowPathToStages = (value: string) =>
  value
    .split("->")
    .map((stage) => stage.trim())
    .filter(Boolean);

const displayValue = (value?: React.ReactNode) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "string" && !value.trim()) return "-";
  return value;
};

const stageDetails: Record<string, { title: string; helper: string }> = {
  M: { title: "Maker", helper: "Creates the request" },
  C: { title: "Checker", helper: "Checks and validates the request" },
  A: { title: "Approver", helper: "Approves or rejects the request" },
  U: { title: "Auditor", helper: "Reviews audit and exception checks" },
};

const parseAmountCondition = (condition: string) => {
  const operator = condition.includes("<=")
    ? "Less Than or Equal (<=)"
    : condition.includes(">=")
    ? "Greater Than or Equal (>=)"
    : "Greater Than (>)";
  const amounts = condition.match(/\u20B9?[\d,]+/g) ?? [];
  const amount = (amounts[amounts.length - 1] ?? "").replace("\u20B9", "");

  return { operator, amount };
};

const getRulePresentation = (index = 0) => {
  const presentations = [
    { icon: <LuRefreshCw />, tone: "bg-[#6687FF1A] text-[#6687FF]" },
    { icon: <LuLandmark />, tone: "bg-[#F59E0B1A] text-[#F97316]" },
    { icon: <LuCalendar />, tone: "bg-[#DBEAFE] text-[#0284C7]" },
    { icon: <LuUsers />, tone: "bg-[#DCFCE7] text-[#16A34A]" },
    { icon: <LuShield />, tone: "bg-[#FCE7F3] text-[#EF4444]" },
  ];
  return presentations[index % presentations.length];
};

const serializeRule = (rule: ApprovalRule): StoredApprovalRule => ({
  name: rule.name,
  appliesTo: rule.appliesTo,
  type: rule.type,
  condition: rule.condition,
  workflow: rule.workflow,
  priority: rule.priority,
  status: rule.status,
});

const hydrateApprovalRules = (): ApprovalRule[] => {
  if (typeof window === "undefined") return [];

  try {
    const stored = window.localStorage.getItem(APPROVAL_RULES_STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored) as StoredApprovalRule[];
    if (!Array.isArray(parsed)) return [];

    const storedRules = sanitizeApprovalRules(parsed).map((rule, index) => ({
      ...rule,
      priority: normalizePriority(rule.priority),
      ...getRulePresentation(index),
    }));
    return storedRules;
  } catch {
    return [];
  }
};

const persistApprovalRules = (nextRules: ApprovalRule[]) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    APPROVAL_RULES_STORAGE_KEY,
    JSON.stringify(nextRules.map(serializeRule)),
  );
};

const hydrateRuleMatrixRows = (): RuleMatrixRow[] => {
  if (typeof window === "undefined") return [];

  try {
    const stored = window.localStorage.getItem(RULE_MATRIX_STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored) as RuleMatrixRow[];
    if (!Array.isArray(parsed) || parsed.length === 0) return [];

    return sanitizeRuleMatrixRows(parsed)
      .filter(
        (row) =>
          row &&
          typeof row.amount === "string" &&
          typeof row.condition === "string" &&
          Array.isArray(row.workflow),
      )
      .map((row) => ({
        amount: row.amount,
        condition: row.condition,
        workflow: row.workflow,
        priority: normalizePriority(row.priority),
        status: row.status === "Inactive" ? "Inactive" : "Active",
      }));
  } catch {
    return [];
  }
};

const persistRuleMatrixRows = (rows: RuleMatrixRow[]) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(RULE_MATRIX_STORAGE_KEY, JSON.stringify(rows));
};

const stageClassName: Record<string, string> = {
  M: "bg-[#EDE9FE] text-[#6D28D9]",
  C: "bg-[#DBEAFE] text-[#0284C7]",
  A: "bg-[#DCFCE7] text-[#16A34A]",
  U: "bg-[#FFEDD5] text-[#F97316]",
};

const tabClass = ({ isActive }: { isActive: boolean }) =>
  "relative -mb-px flex h-12 items-center border-b-2 px-4 text-sm font-semibold " +
  (isActive
    ? "border-[#6687FF] text-[#6687FF]"
    : "border-transparent text-[#505E8E]");

function StagePath({ stages }: { stages: string[] }) {
  if (!stages.length) {
    return <span className="font-semibold text-[#505E8E]">-</span>;
  }

  return (
    <div className="flex items-center gap-2">
      {stages.map((stage, index) => (
        <React.Fragment key={`${stage}-${index}`}>
          <span
            className={
              "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold " +
              stageClassName[stage]
            }
          >
            {stage}
          </span>
          {index < stages.length - 1 && (
            <span className="text-xs text-[#A3AED0]">{"->"}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function SelectBox({ label, value }: { label: string; value: string }) {
  return <PayoutDropdown label={label} value={value} className="min-w-[170px]" />;
}

type FloatingActionMenuState = {
  id: string;
  top: number;
  left: number;
} | null;

const actionMenuItems = [
  ["view", "View Details"],
  ["edit", "Edit"],
  ["delete", "Delete"],
];

function getActionMenuPosition(
  event: React.MouseEvent<HTMLButtonElement>,
  id: string,
): Exclude<FloatingActionMenuState, null> {
  const rect = event.currentTarget.getBoundingClientRect();
  const menuWidth = 176;
  const menuHeight = 132;
  const opensUp = rect.bottom + menuHeight + 12 > window.innerHeight;

  return {
    id,
    top: opensUp ? Math.max(12, rect.top - menuHeight - 8) : rect.bottom + 8,
    left: Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12)),
  };
}

function FloatingActionMenuButton({
  id,
  label,
  openMenu,
  setOpenMenu,
  onAction,
}: {
  id: string;
  label: string;
  openMenu: FloatingActionMenuState;
  setOpenMenu: React.Dispatch<React.SetStateAction<FloatingActionMenuState>>;
  onAction: (action: string) => void;
}) {
  const menu =
    openMenu?.id === id && typeof document !== "undefined" ? (
      <div
        className="fixed z-[9999] w-44 overflow-hidden rounded-[8px] border border-[#E0E5F2] bg-white py-2 text-sm font-semibold text-[#1B163B] shadow-[0_14px_28px_rgba(27,22,59,0.18)]"
        style={{ top: openMenu.top, left: openMenu.left }}
      >
        {actionMenuItems.map(([action, actionLabel]) => (
          <button
            key={action}
            type="button"
            onClick={() => onAction(action)}
            className={
              "block w-full px-4 py-2.5 text-left hover:bg-[#F4F7FE] " +
              (action === "delete" ? "text-[#A04444]" : "")
            }
          >
            {actionLabel}
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className="flex items-center justify-center">
      <button
        type="button"
        onClick={(event) =>
          setOpenMenu((current) =>
            current?.id === id ? null : getActionMenuPosition(event, id),
          )
        }
        className="flex h-9 w-9 items-center justify-center rounded-[6px] hover:bg-[#F4F7FE]"
        aria-label={`${label} actions`}
        title={`${label} actions`}
      >
        <FiMoreVertical className="text-lg" />
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}

function DeleteVerificationModal({
  itemName,
  itemType,
  onCancel,
  onDelete,
}: {
  itemName: string;
  itemType: string;
  onCancel: () => void;
  onDelete: () => void;
}) {
  const [step, setStep] = React.useState<"confirm" | "verify" | "success">(
    "confirm",
  );
  const [method, setMethod] = React.useState<"authenticator" | "sms">(
    "authenticator",
  );
  const [code, setCode] = React.useState<string[]>(Array(6).fill(""));
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setStep("confirm");
    setMethod("authenticator");
    setCode(Array(6).fill(""));
    setError("");
  }, [itemName, itemType]);

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
    if (digit && index < 5) {
      const next = document.getElementById(
        `approval-delete-otp-${index + 1}`,
      ) as HTMLInputElement | null;
      next?.focus();
    }
  };

  const handleVerifyDelete = () => {
    if (code.join("").length !== 6) {
      setError("Enter the 6-digit verification code before deleting.");
      return;
    }
    onDelete();
    setStep("success");
    setError("");
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#1B163B]/35 px-4">
      <div className="w-full max-w-[560px] rounded-[10px] border border-[#E0E5F2] bg-white p-6 shadow-2xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[22px] font-semibold text-[#1B163B]">
              {step === "confirm"
                ? `Delete ${itemType}`
                : step === "verify"
                  ? "Verify Deletion"
                  : `${itemType} Deleted`}
            </h2>
            <p className="mt-2 text-sm font-medium text-[#505E8E]">
              {step === "confirm"
                ? `This will remove "${itemName}" from the approval configuration.`
                : step === "verify"
                  ? "Verify this action before the deletion is completed."
                  : `"${itemName}" has been deleted.`}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-xl font-semibold text-[#1B163B]"
            aria-label="Close delete dialog"
          >
            x
          </button>
        </div>

        {step === "confirm" && (
          <>
            <div className="rounded-[8px] border border-[#F59E0B66] bg-[#FEF3C7] px-4 py-3 text-sm font-medium text-[#92400E]">
              Are you sure you want to delete this {itemType}? This action will
              require verification before it is applied.
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep("verify")}
                style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
                className="h-10 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === "verify" && (
          <>
            {error && (
              <p className="mb-4 rounded-[6px] border border-[#F59E0B66] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
                {error}
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  key: "authenticator",
                  title: "Google Authenticator",
                  helper: "Use your authenticator app",
                  icon: <LuShield />,
                },
              ].map((option) => {
                const active = method === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setMethod("authenticator")}
                    className={
                      "flex min-h-[86px] items-center justify-between rounded-[8px] border px-4 text-left " +
                      (active
                        ? "border-[#1B163B] bg-white"
                        : "border-[#E0E5F2] bg-white")
                    }
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-[#F4F7FE] text-[#1B163B]">
                        {option.icon}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-[#1B163B]">
                          {option.title}
                        </span>
                        <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                          {option.helper}
                        </span>
                      </span>
                    </span>
                    {active && (
                      <span className="text-sm font-semibold text-[#1B163B]">
                        {"\u2713"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-6">
              <FieldLabel required>Verification Code</FieldLabel>
              <div className="flex flex-wrap gap-3">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    id={`approval-delete-otp-${index}`}
                    value={digit}
                    onChange={(event) => handleCodeChange(index, event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Backspace" && !code[index] && index > 0) {
                        const previous = document.getElementById(
                          `approval-delete-otp-${index - 1}`,
                        ) as HTMLInputElement | null;
                        previous?.focus();
                      }
                    }}
                    className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                    maxLength={1}
                    inputMode="numeric"
                  />
                ))}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setStep("confirm");
                  setError("");
                }}
                className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleVerifyDelete}
                style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
                className="h-10 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
              >
                Verify & Delete
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <div className="text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#EAF2EE] text-xl font-semibold text-[#2B6B47]">
              {"\u2713"}
            </span>
            <p className="mx-auto mt-5 max-w-[420px] text-sm font-medium text-[#505E8E]">
              The {itemType} was deleted successfully after verification.
            </p>
            <button
              type="button"
              onClick={onCancel}
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="mt-6 h-10 rounded-[6px] bg-[#1B163B] px-6 text-sm font-semibold text-white"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function ApprovalTabs() {
  return (
    <div className="mb-6 flex items-center gap-4 border-b border-[#E0E5F2]">
      <NavLink end to="/payouts/approval-rules" className={tabClass}>
        Workflow Overview
      </NavLink>
      <NavLink to="/payouts/approval-rules/workflow-rules" className={tabClass}>
        Approval Workflows
      </NavLink>
      <NavLink to="/payouts/approval-rules/approval-groups" className={tabClass}>
        Approval Groups
      </NavLink>
      <NavLink to="/payouts/approval-rules/rule-matrix" className={tabClass}>
        Workflow Matrix
      </NavLink>
    </div>
  );
}

function WorkflowOverview({
  approvalRules,
  onRulesChange,
  workflowStats: workflowStatsProp = workflowStats,
}: {
  approvalRules: ApprovalRule[];
  onRulesChange: (updater: (current: ApprovalRule[]) => ApprovalRule[]) => void;
  workflowStats?: typeof workflowStats;
}) {
  const navigate = useNavigate();
  const [openWorkflowMenu, setOpenWorkflowMenu] =
    React.useState<FloatingActionMenuState>(null);
  const [message, setMessage] = React.useState("");
  const [deleteWorkflow, setDeleteWorkflow] =
    React.useState<ApprovalRule | null>(null);

  const handleWorkflowAction = (workflow: ApprovalRule, action: string) => {
    setOpenWorkflowMenu(null);
    if (action === "view") {
      navigate(getRuleDetailsPath(workflow));
      return;
    }
    if (action === "edit") {
      navigate(getRuleEditPath(workflow));
      return;
    }
    if (action === "delete") {
      setDeleteWorkflow(workflow);
    }
  };

  const workflowTableRows = approvalRules.slice(0, 4).map((workflow) => [
    <div className="flex items-center gap-4">
      <span
        className={
          "flex h-9 w-9 items-center justify-center rounded-[6px] text-base " +
          workflow.tone
        }
      >
        {workflow.icon}
      </span>
      <span className="min-w-0">
        <PayoutTableCell
          value={displayValue(workflow.name)}
          valueClassName="font-semibold text-[#1B163B]"
        />
        <span
          className="mt-1 block max-w-[220px] truncate text-[#505E8E]"
          title={String(displayValue(workflow.condition))}
        >
          {displayValue(workflow.condition)}
        </span>
      </span>
    </div>,
    displayValue(workflow.appliesTo),
    displayValue(workflow.type),
    <StagePath stages={workflow.workflow} />,
    <span className="rounded-[5px] bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#16A34A]">
      {displayValue(workflow.status)}
    </span>,
    <FloatingActionMenuButton
      id={getRuleRouteKey(workflow)}
      label={workflow.name}
      openMenu={openWorkflowMenu}
      setOpenMenu={setOpenWorkflowMenu}
      onAction={(action) => handleWorkflowAction(workflow, action)}
    />,
  ]);
  const backendStructureStages = approvalRules[0]?.workflow?.length
    ? approvalRules[0].workflow
    : [];
  const structureStages =
    backendStructureStages.length && !backendStructureStages.includes("M")
      ? ["M", ...backendStructureStages]
      : backendStructureStages;
  const structureLabel = structureStages.length ? structureStages.join(" -> ") : "-";

  return (
    <>
      <ApprovalTabs />
      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        {workflowStatsProp.map((stat) => (
          <div
            key={stat.label}
            className="flex min-h-[120px] items-center gap-6 rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-5 new-shadow"
          >
            <div
              className={
                "payout-kpi-icon flex h-11 w-11 shrink-0 items-center justify-center text-xl " +
                stat.tone
              }
            >
              {stat.icon}
            </div>
            <div>
              <p className="text-sm font-semibold text-[#505E8E]">
                {stat.label}
              </p>
              <p className="mt-2 text-[24px] font-semibold leading-8 text-[#1B163B]">
                {stat.value}
              </p>
              <p className="mt-2 text-xs font-medium text-[#505E8E]">
                {stat.helper}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-6 grid gap-5 xl:grid-cols-[minmax(0,1fr)_390px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Current Approval Workflows
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                View and manage your organization's approval workflows
              </p>
            </div>
            <Link
              to="/payouts/approval-rules/workflow-rules"
              className="mt-1 inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-[#6687FF]"
            >
              View All Workflows
              <FaArrowRight className="text-xs" />
            </Link>
          </div>
          {message && (
            <p className="mb-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
              {message}
            </p>
          )}
          <PayoutDataTable
            columns={[
              "Workflow Name",
              "Applies To",
              "Workflow Type",
              "Stages",
              "Status",
              "Actions",
            ]}
            rows={workflowTableRows}
            minWidth="min-w-[760px]"
          />
          {deleteWorkflow && (
            <DeleteVerificationModal
              itemName={deleteWorkflow.name}
              itemType="workflow"
              onCancel={() => setDeleteWorkflow(null)}
              onDelete={() => {
                onRulesChange((current) =>
                  current.filter(
                    (workflow) =>
                      getRuleRouteKey(workflow) !== getRuleRouteKey(deleteWorkflow),
                  ),
                );
                setMessage(`${deleteWorkflow.name} deleted after verification.`);
              }}
            />
          )}
          <div className="hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] table-auto">
              <thead className="bg-[#A9B2CF1A] text-center">
                <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                  <th className="px-4 py-3.5 text-center">Workflow Name</th>
                  <th className="px-4 py-3.5 text-center">Applies To</th>
                  <th className="px-4 py-3.5 text-center">Workflow Type</th>
                  <th className="px-4 py-3.5 text-center">Stages</th>
                  <th className="px-4 py-3.5 text-center">Status</th>
                  <th className="px-4 py-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {approvalRules.map((workflow) => (
                  <tr
                    key={workflow.name}
                    className="border-t border-[#E0E5F2] text-xs font-medium text-[#1B163B]"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-4">
                        <span
                          className={
                            "flex h-9 w-9 items-center justify-center rounded-[6px] text-base " +
                            workflow.tone
                          }
                        >
                          {workflow.icon}
                        </span>
                        <span>
                          <span className="block font-semibold">
                            {workflow.name}
                          </span>
                          <span className="mt-1 block text-[#505E8E]">
                            {workflow.condition}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">{workflow.appliesTo}</td>
                    <td className="px-4 py-4">{workflow.type}</td>
                    <td className="px-4 py-4">
                      <StagePath stages={workflow.workflow} />
                    </td>
                    <td className="px-4 py-4">
                      <span className="rounded-[5px] bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#16A34A]">
                        {displayValue(workflow.status)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center">
                        <button>
                          <FiMoreVertical className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </section>

        <aside className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Workflow Structure
          </h2>
          <p className="mt-2 text-sm font-semibold text-[#1B163B]">
            {structureLabel}
          </p>
          <p className="mt-1 text-xs font-medium text-[#505E8E]">
            {structureStages.length
              ? "Derived from the current backend workflow"
              : "No workflow data available"}
          </p>
          {structureStages.map((stage, index) => (
            <div key={stage} className="relative mt-6 flex gap-5">
              {index < structureStages.length - 1 && (
                <span className="absolute left-[18px] top-10 h-12 w-px bg-[#D6DCEB]" />
              )}
              <span
                className={
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold " +
                  stageClassName[stage]
                }
              >
                {stage}
              </span>
              <div>
                <p className="text-sm font-semibold text-[#1B163B]">
                  {stageDetails[stage]?.title || stage}
                </p>
                <p className="mt-1 text-xs font-medium text-[#505E8E]">
                  {stageDetails[stage]?.helper || "-"}
                </p>
              </div>
            </div>
          ))}
          <div className="mt-6 flex items-center gap-3 rounded-[6px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#6687FF]">
            <FiInfo />
            {structureStages.length
              ? "Workflow stages are shown from backend data."
              : "No workflow stages were returned by the backend."}
          </div>
        </aside>
      </div>

    </>
  );
}

function WorkflowRules({
  approvalRules,
  onRulesChange,
}: {
  approvalRules: ApprovalRule[];
  onRulesChange: (updater: (current: ApprovalRule[]) => ApprovalRule[]) => void;
}) {
  const navigate = useNavigate();
  const [openRuleMenu, setOpenRuleMenu] =
    React.useState<FloatingActionMenuState>(null);
  const [message, setMessage] = React.useState("");
  const [deleteRule, setDeleteRule] = React.useState<ApprovalRule | null>(null);

  const handleRuleAction = (rule: ApprovalRule, action: string) => {
    setOpenRuleMenu(null);
    if (action === "view") {
      navigate(getRuleDetailsPath(rule));
      return;
    }
    if (action === "edit") {
      navigate(getRuleEditPath(rule));
      return;
    }
    if (action === "delete") {
      setDeleteRule(rule);
    }
  };

  const ruleTableRows = approvalRules.map((rule) => [
    <div className="flex items-center gap-4">
      <span
        className={
          "flex h-9 w-9 items-center justify-center rounded-[6px] text-base " +
          rule.tone
        }
      >
        {rule.icon}
      </span>
      <PayoutTableCell
        value={displayValue(rule.name)}
        valueClassName="font-semibold text-[#1B163B]"
      />
    </div>,
    displayValue(rule.appliesTo),
    displayValue(rule.type),
    <span
      className="block max-w-[220px] truncate rounded-[5px] bg-[#F4F7FE] px-2 py-1 text-xs font-semibold text-[#505E8E]"
      title={String(displayValue(rule.condition))}
    >
      {displayValue(rule.condition)}
    </span>,
    <StagePath stages={rule.workflow} />,
    <span className="font-semibold">{displayValue(rule.priority)}</span>,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        (rule.status === "Active"
          ? "bg-[#DCFCE7] text-[#16A34A]"
          : "bg-[#E5E7EB] text-[#505E8E]")
      }
    >
      {rule.status}
    </span>,
    <FloatingActionMenuButton
      id={getRuleRouteKey(rule)}
      label={rule.name}
      openMenu={openRuleMenu}
      setOpenMenu={setOpenRuleMenu}
      onAction={(action) => handleRuleAction(rule, action)}
    />,
  ]);

  return (
    <>
      <ApprovalTabs />
      <section className="mb-6 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Approval Workflows
            </h2>
            <p className="mt-1 text-sm font-medium text-[#505E8E]">
              Define workflows that determine how transfers are approved based on
              conditions.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/payouts/approval-rules/workflow-rules/new"
              className="flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white"
            >
              <FiPlus className="text-lg" />
              New Workflow
            </Link>
          </div>
        </div>
        {message && (
          <p className="mb-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
            {message}
          </p>
        )}
        <PayoutDataTable
          columns={[
            "Workflow Name",
            "Applies To",
            "Workflow Type",
            "Conditions",
            "Workflow",
            "Priority",
            "Status",
            "Actions",
          ]}
          rows={ruleTableRows}
          minWidth="min-w-[980px]"
        footer={
            <div className="flex min-h-[52px] w-full items-center justify-between gap-4 bg-white px-5 py-4 text-xs font-medium text-[#505E8E]">
              <span className="min-w-max">
                Showing {approvalRules.length ? 1 : 0} to {approvalRules.length} of {approvalRules.length} workflows
              </span>
              <div className="ml-auto flex min-w-max items-center gap-4">
                <button className="flex h-9 min-w-[120px] items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-medium text-[#1B163B]">
                  10 per page
                  <FiChevronDown />
                </button>
                <div className="flex items-center gap-2">
                  <button className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#A3AED0]">
                    {"<"}
                  </button>
                  <button className="h-9 w-9 rounded-md border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]">
                    1
                  </button>
                  <button className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#A3AED0]">
                    {">"}
                  </button>
                </div>
              </div>
            </div>
          }
        />
        {deleteRule && (
          <DeleteVerificationModal
            itemName={deleteRule.name}
            itemType="workflow"
            onCancel={() => setDeleteRule(null)}
            onDelete={() => {
              onRulesChange((current) =>
                current.filter(
                  (item) => getRuleRouteKey(item) !== getRuleRouteKey(deleteRule),
                ),
              );
              setMessage(`${deleteRule.name} deleted after verification.`);
            }}
          />
        )}
        <div className="hidden overflow-x-auto">
          <table className="w-full min-w-[980px] table-auto">
            <thead className="bg-[#A9B2CF1A] text-center">
              <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                <th className="px-4 py-3.5 text-center">Workflow Name</th>
                <th className="px-4 py-3.5 text-center">Applies To</th>
                <th className="px-4 py-3.5 text-center">Workflow Type</th>
                <th className="px-4 py-3.5 text-center">Conditions</th>
                <th className="px-4 py-3.5 text-center">Workflow</th>
                <th className="px-4 py-3.5 text-center">Priority</th>
                <th className="px-4 py-3.5 text-center">Status</th>
                <th className="px-4 py-3.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvalRules.map((rule) => (
                <tr
                  key={rule.name}
                  className="border-t border-[#E0E5F2] text-xs font-medium text-[#1B163B]"
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-4">
                      <span
                        className={
                          "flex h-9 w-9 items-center justify-center rounded-[6px] text-base " +
                          rule.tone
                        }
                      >
                        {rule.icon}
                      </span>
                      <span className="font-semibold">{rule.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">{rule.appliesTo}</td>
                  <td className="px-4 py-4">{rule.type}</td>
                  <td className="px-4 py-4">
                    <span className="rounded-[5px] bg-[#F4F7FE] px-2 py-1 text-xs font-semibold text-[#505E8E]">
                      {rule.condition}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <StagePath stages={rule.workflow} />
                  </td>
                  <td className="px-4 py-4 font-semibold">{rule.priority}</td>
                  <td className="px-4 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        (rule.status === "Active"
                          ? "bg-[#DCFCE7] text-[#16A34A]"
                          : "bg-[#E5E7EB] text-[#505E8E]")
                      }
                    >
                      {rule.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center">
                      <button>
                        <FiMoreVertical className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </>
  );
}

function RuleDetailMetric({
  icon,
  label,
  value,
  helper,
  tone = "bg-[#6687FF1A] text-[#6687FF]",
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
  tone?: string;
}) {
  return (
    <div className="flex min-h-[86px] items-center gap-5 rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-4 new-shadow">
      <span
        className={"payout-kpi-icon flex h-10 w-10 shrink-0 items-center justify-center text-lg " + tone}
      >
        {icon}
      </span>
      <div>
        <p className="text-xs font-semibold text-[#505E8E]">{label}</p>
        <div className="mt-2 text-sm font-semibold text-[#1B163B]">{value}</div>
        {helper && <div className="mt-2 text-xs font-medium text-[#505E8E]">{helper}</div>}
      </div>
    </div>
  );
}

function RuleDetailsPanel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
      <h2 className="text-[18px] font-semibold text-[#1B163B]">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ViewRuleDetails({
  approvalRules,
  approvalGroups,
  onSave,
}: {
  approvalRules: ApprovalRule[];
  approvalGroups: ApprovalGroup[];
  onSave: (
    rule: ApprovalRule,
    previousRouteKey?: string,
    stepUpToken?: string,
  ) => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const stepUpRequired = isPayoutStepUpEnabled();
  const { ruleName } = useParams();
  const decodedRuleName = ruleName ? decodeURIComponent(ruleName) : "";
  const rule = findRuleByRouteKey(approvalRules, decodedRuleName) as
    | ApprovalRule
    | undefined;
  const [isDeactivateVerifying, setIsDeactivateVerifying] = React.useState(false);
  const [verificationMethod, setVerificationMethod] = React.useState<
    "authenticator" | "sms"
  >("authenticator");
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill(""),
  );
  const [verificationMessage, setVerificationMessage] = React.useState("");
  const [isSubmittingDeactivate, setIsSubmittingDeactivate] = React.useState(false);

  if (!rule) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 text-sm font-semibold text-[#505E8E]">
          <Link to="/payouts/approval-rules">Approval Workflows</Link>
          <span>{">"}</span>
          <Link to="/payouts/approval-rules/workflow-rules">Approval Workflows</Link>
          <span>{">"}</span>
          <span className="text-[#1B163B]">View Workflow Details</span>
        </div>
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h1 className="text-[22px] font-semibold text-[#1B163B]">
            Workflow not found
          </h1>
          <p className="mt-2 text-sm font-medium text-[#505E8E]">
            The selected workflow is unavailable or still loading.
          </p>
          <button
            type="button"
            onClick={() => navigate("/payouts/approval-rules/workflow-rules")}
            className="mt-5 flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
          >
            <FiArrowLeft />
            Back to Workflows
          </button>
        </section>
      </div>
    );
  }

  const assignedApprovalGroup =
    approvalGroups.find((group) => group.id === rule.approvalGroupId) ||
    approvalGroups.find(
      (group) =>
        rule.approvalGroupName &&
        group.name.trim().toLowerCase() === rule.approvalGroupName.trim().toLowerCase(),
    ) ||
    approvalGroups.find(
      (group) =>
        group.flow
          .split(" -> ")
          .map((stage) => stage[0])
          .join("") === rule.workflow.join(""),
    );
  const assignedApprovalGroupName =
    assignedApprovalGroup?.name || rule.approvalGroupName || "-";
  const verificationCodeValue = verificationCode.join("");

  const handleDeactivateCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setVerificationCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
    if (digit && index < 5) {
      const next = document.getElementById(
        `approval-rule-deactivate-otp-${index + 1}`,
      ) as HTMLInputElement | null;
      next?.focus();
    }
  };

  const handleDeactivateKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !verificationCode[index] && index > 0) {
      const previous = document.getElementById(
        `approval-rule-deactivate-otp-${index - 1}`,
      ) as HTMLInputElement | null;
      previous?.focus();
    }
  };

  const handleDeactivateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (verificationCodeValue.length !== 6) {
      setVerificationMessage(
        "Enter the 6-digit verification code before deactivating this workflow.",
      );
      return;
    }
    try {
      setIsSubmittingDeactivate(true);
      const stepUpToken =
        isPayoutApiMode() && stepUpRequired
          ? await verifyStepUp({
              purpose: "DEACTIVATE_APPROVAL_WORKFLOW",
              method: verificationMethod,
              code: verificationCodeValue,
              subjectId: getRuleRouteKey(rule),
            })
          : undefined;
      await onSave(
        { ...rule, status: "Inactive" },
        getRuleRouteKey(rule),
        stepUpToken,
      );
      navigate("/payouts/approval-rules/workflow-rules");
    } catch (error) {
      setVerificationMessage(
        errorMessage(error, "Unable to deactivate this workflow."),
      );
    } finally {
      setIsSubmittingDeactivate(false);
    }
  };

  const conditions = [
    ["Amount", ">=", rule.condition.replace("Amount >", "").replace("Amount <=", "").trim() || "-", <FiSettings />],
  ];

  if (isDeactivateVerifying) {
    return (
      <form
        onSubmit={handleDeactivateSubmit}
        className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]"
      >
        <div className="space-y-5">
          <div className="flex items-center gap-3 text-sm font-semibold text-[#505E8E]">
            <Link to="/payouts/approval-rules">Approval Workflows</Link>
            <span>{">"}</span>
            <Link to="/payouts/approval-rules/workflow-rules">Approval Workflows</Link>
            <span>{">"}</span>
            <span className="text-[#1B163B]">Verify Deactivation</span>
          </div>
          {verificationMessage && (
            <p className="rounded-[6px] border border-[#F59E0B66] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
              {verificationMessage}
            </p>
          )}
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <div className="mb-6 flex items-start gap-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-[8px] bg-[#F4F7FE] text-xl text-[#1B163B]">
                <LuShield />
              </span>
              <div>
                <h1 className="text-[22px] font-semibold text-[#1B163B]">
                  Verify Workflow Deactivation
                </h1>
                <p className="mt-2 text-sm font-medium text-[#505E8E]">
                  Verify this workflow before it is deactivated.
                </p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {[
                {
                  key: "authenticator",
                  title: "Google Authenticator",
                  helper: "Recommended for approval workflow changes",
                  icon: <LuShield />,
                },
              ].map((method) => {
                const active = verificationMethod === method.key;
                return (
                  <button
                    key={method.key}
                    type="button"
                    onClick={() => setVerificationMethod("authenticator")}
                    className={
                      "flex min-h-[92px] items-center justify-between rounded-[8px] border px-5 text-left " +
                      (active
                        ? "border-[#1B163B] bg-white"
                        : "border-[#E0E5F2] bg-white")
                    }
                  >
                    <span className="flex items-center gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-[8px] bg-[#F4F7FE] text-lg text-[#6687FF]">
                        {method.icon}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-[#1B163B]">
                          {method.title}
                        </span>
                        <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                          {method.helper}
                        </span>
                      </span>
                    </span>
                    {active && (
                      <span className="text-lg font-semibold text-[#166534]">
                        {"\u2713"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-8">
              <FieldLabel required>Verification Code</FieldLabel>
              <div className="flex flex-wrap gap-3">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`approval-rule-deactivate-otp-${index}`}
                    value={digit}
                    onChange={(event) =>
                      handleDeactivateCodeChange(index, event.target.value)
                    }
                    onKeyDown={(event) => handleDeactivateKeyDown(index, event)}
                    className="h-14 w-14 rounded-[8px] border border-[#E0E5F2] bg-white text-center text-xl font-semibold text-[#1B163B] outline-none focus:border-[#6687FF]"
                    maxLength={1}
                    inputMode="numeric"
                  />
                ))}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-5">
                <button
                  type="button"
                  onClick={() => setVerificationCode(Array(6).fill(""))}
                  className="text-sm font-semibold text-[#1B163B]"
                >
                  Resend code
                </button>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[#E0E5F2] pt-5">
              <button
                type="button"
                onClick={() => {
                  setIsDeactivateVerifying(false);
                  setVerificationMessage("");
                  setVerificationCode(Array(6).fill(""));
                }}
                className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
              >
                <FiArrowLeft />
                Back to Details
              </button>
              <button
                type="submit"
                disabled={verificationCodeValue.length !== 6 || isSubmittingDeactivate}
                className="h-10 rounded-[6px] bg-edviron_black px-6 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmittingDeactivate ? "Deactivating..." : "Verify & Deactivate"}
              </button>
            </div>
          </section>
        </div>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
            Verification Summary
          </h2>
          <div className="mb-5 h-px bg-[#E0E5F2]" />
          {[
            ["Workflow", rule.name],
            ["Current Status", rule.status],
            ["Approval Group", assignedApprovalGroupName],
            [
              "Verification",
              "Google Authenticator",
            ],
          ].map(([label, value]) => (
            <div key={label} className="mb-4 flex justify-between gap-4 text-sm">
              <span className="font-medium text-[#505E8E]">{label}</span>
              <span className="text-right font-semibold text-[#1B163B]">
                {value}
              </span>
            </div>
          ))}
          <div className="mt-5 rounded-[6px] bg-[#FEE2E2] px-4 py-3 text-sm font-medium text-[#991B1B]">
            Deactivation will stop this workflow from being used for new
            matching payouts.
          </div>
        </aside>
      </form>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 text-sm font-semibold text-[#505E8E]">
        <Link to="/payouts/approval-rules">Approval Workflows</Link>
        <span>{">"}</span>
        <Link to="/payouts/approval-rules/workflow-rules">Approval Workflows</Link>
        <span>{">"}</span>
        <span className="text-[#1B163B]">View Workflow Details</span>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[28px] font-semibold leading-9 text-[#1B163B]">
              {rule.name}
            </h1>
            <span
              className={
                "rounded-[5px] px-3 py-1 text-sm font-semibold " +
                (rule.status === "Active"
                  ? "bg-[#DCFCE7] text-[#166534]"
                  : "bg-[#E5E7EB] text-[#505E8E]")
              }
            >
              {rule.status}
            </span>
          </div>
          <p className="mt-2 text-sm font-medium text-[#505E8E]">
            {displayValue(rule.description || rule.condition)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/payouts/approval-rules/workflow-rules")}
            className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
          >
            <FiArrowLeft />
            Back to Workflows
          </button>
          <Link
            to={getRuleEditPath(rule)}
            className="flex h-10 items-center gap-2 rounded-[6px] bg-edviron_black px-4 text-sm font-semibold text-white"
          >
            <FiEdit2 />
            Edit Workflow
          </Link>
          <button
            type="button"
            onClick={() => {
              setIsDeactivateVerifying(true);
              setVerificationCode(Array(6).fill(""));
              setVerificationMessage("");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex h-10 items-center gap-2 rounded-[6px] border border-[#FCA5A5] bg-white px-4 text-sm font-semibold text-[#DC2626]"
          >
            <FiPower />
            Deactivate Workflow
          </button>
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-edviron_black text-white"
          >
            <FiChevronDown />
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <RuleDetailMetric
          icon={<LuCalendar />}
          label="Workflow ID"
          value={rule.ruleId || rule.workflowId || "-"}
        />
        <RuleDetailMetric
          icon={<LuUserCog />}
          label="Created By"
          value={rule.createdBy || "-"}
        />
        <RuleDetailMetric
          icon={<LuCalendar />}
          label="Created On"
          value={rule.createdAt || "-"}
        />
        <RuleDetailMetric
          icon={<FiClock />}
          label="Last Updated"
          value={rule.lastUpdated || "-"}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <RuleDetailMetric icon={<LuShield />} label="Applies To" value={rule.appliesTo} />
        <RuleDetailMetric icon={<LuList />} label="Workflow Type" value="Amount Based" />
        <RuleDetailMetric icon={<FiSettings />} label="Priority" value={rule.priority} />
        <RuleDetailMetric
          icon={<LuShield />}
          label="Status"
          value={<span className="text-[#166534]">{rule.status}</span>}
          tone="bg-[#DCFCE7] text-[#166534]"
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_390px]">
        <div className="space-y-5">
          <RuleDetailsPanel title="Conditions">
            <p className="-mt-1 mb-5 text-sm font-medium text-[#505E8E]">
              All the conditions below must be met for this workflow to apply.
            </p>
            <div className="relative rounded-[8px] border border-[#E0E5F2]">
              {conditions.map(([label, operator, value, icon], index) => (
                <div
                  key={String(label)}
                  className="relative grid min-h-[58px] grid-cols-[48px_1fr_70px_1fr] items-center gap-3 border-b border-[#E0E5F2] px-4 last:border-b-0"
                >
                  {index > 0 && (
                    <span className="absolute -left-1 top-[-12px] rounded-[4px] border border-[#E0E5F2] bg-white px-1.5 py-0.5 text-xs font-semibold text-[#505E8E]">
                      AND
                    </span>
                  )}
                  <span className="flex h-9 w-9 items-center justify-center rounded-[6px] bg-[#F4F7FE] text-[#6687FF]">
                    {icon as React.ReactNode}
                  </span>
                  <span className="font-semibold text-[#1B163B]">{String(label)}</span>
                  <span className="font-semibold text-[#505E8E]">{String(operator)}</span>
                  <span className="font-semibold text-[#1B163B]">{String(value)}</span>
                </div>
              ))}
            </div>
          </RuleDetailsPanel>

          <RuleDetailsPanel title="Workflow Configuration">
            <div className="grid gap-y-5 text-sm md:grid-cols-[170px_minmax(0,1fr)]">
              {[
                ["Matching Logic", "All Conditions (AND)"],
                ["Priority", rule.priority || "-"],
                ["Effective From", "-"],
                ["Effective To", "-"],
                [
                  "Description",
                  rule.description || "-",
                ],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <span className="font-semibold text-[#505E8E]">{label}</span>
                  <span className="font-semibold text-[#1B163B]">{value}</span>
                </React.Fragment>
              ))}
            </div>
          </RuleDetailsPanel>

        </div>

        <div className="space-y-5">
          <RuleDetailsPanel title="Approval Group Assigned">
            <p className="-mt-1 mb-5 text-sm font-medium text-[#505E8E]">
              This workflow will use the following approval group.
            </p>
            <div className="rounded-[8px] border border-[#E0E5F2] px-4 py-4">
              <p className="text-xs font-semibold text-[#505E8E]">
                Approval Group
              </p>
              <p className="mt-2 text-sm font-semibold text-[#1B163B]">
                {assignedApprovalGroupName}
              </p>
              <button
                type="button"
                onClick={() => {
                  if (assignedApprovalGroup) {
                    navigate(getApprovalGroupDetailsPath(assignedApprovalGroup.id));
                  }
                }}
                disabled={!assignedApprovalGroup}
                className="mt-5 flex h-10 w-full items-center justify-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#6687FF]"
              >
                View Group <FiExternalLink />
              </button>
            </div>
          </RuleDetailsPanel>
        </div>
      </div>

    </div>
  );
}

function RuleMatrix({ matrixItems }: { matrixItems: RuleMatrixRow[] }) {
  const [amountFilter, setAmountFilter] = React.useState("All");
  const filteredMatrixItems = React.useMemo(
    () =>
      amountFilter === "All"
        ? matrixItems
        : matrixItems.filter((row) => row.amount === amountFilter),
    [amountFilter, matrixItems],
  );
  const amountOptions = React.useMemo(
    () => ["All", ...matrixItems.map((row) => row.amount)],
    [matrixItems],
  );

  return (
    <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Workflow Matrix
          </h2>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            View how workflows are applied across different conditions for
            transfers.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to={getRuleMatrixConfigurePath()}
            className="flex h-10 items-center gap-3 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#6687FF]"
          >
            <FiSettings className="text-lg" />
            Configure Matrix
          </Link>
          <button className="flex h-10 items-center gap-3 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]">
            <FiDownload className="text-lg text-[#505E8E]" />
            Export
          </button>
        </div>
      </div>
      <div className="mb-5 flex flex-wrap gap-5">
        <SelectBox label="Applies To" value="Transfers" />
        <PayoutDropdown
          label="Amount Range"
          value={amountFilter}
          options={amountOptions}
          onChange={setAmountFilter}
          className="min-w-[190px]"
        />
      </div>
      <PayoutFilterChips
        className="mb-5"
        chips={[
          {
            label: "Amount Range",
            value: amountFilter,
            onRemove: () => setAmountFilter("All"),
          },
        ]}
      />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] table-fixed">
          <thead>
            <tr className="bg-[#A9B2CF1A] text-left text-xs font-semibold text-[#1B163B]">
              <th className="border border-[#E0E5F2] px-4 py-5">Amount Range</th>
              <th className="border border-[#E0E5F2] px-4 py-5">Condition</th>
              <th className="border border-[#E0E5F2] px-4 py-5">Approval Workflow</th>
              <th className="border border-[#E0E5F2] px-4 py-5">Priority</th>
              <th className="border border-[#E0E5F2] px-4 py-5">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredMatrixItems.map((row) => (
              <tr key={row.amount} className="text-xs font-medium text-[#1B163B]">
                <td className="border border-[#E0E5F2] px-4 py-5 font-semibold">
                  {displayValue(row.amount)}
                </td>
                <td className="border border-[#E0E5F2] px-4 py-5 text-[#505E8E]">
                  {displayValue(row.condition)}
                </td>
                <td className="border border-[#E0E5F2] px-4 py-5">
                  <StagePath stages={row.workflow} />
                </td>
                <td className="border border-[#E0E5F2] px-4 py-5 font-semibold">
                  {displayValue(row.priority)}
                </td>
                <td className="border border-[#E0E5F2] px-4 py-5">
                  <span
                    className={
                      "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                      (row.status === "Active"
                        ? "bg-[#EAF2EE] text-[#2B6B47]"
                        : "bg-[#E5E7EB] text-[#505E8E]")
                    }
                  >
                    {displayValue(row.status)}
                  </span>
                </td>
              </tr>
            ))}
            {!filteredMatrixItems.length && (
              <tr className="text-xs font-semibold text-[#505E8E]">
                <td className="border border-[#E0E5F2] px-4 py-6 text-center" colSpan={5}>
                  -
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-4 text-sm font-medium text-[#505E8E]">
        <div className="flex flex-wrap items-center gap-6">
          {[
            ["M", "Maker"],
            ["C", "Checker"],
            ["A", "Approver"],
            ["U", "Auditor (Optional)"],
          ].map(([stage, label]) => (
            <span key={stage} className="flex items-center gap-2">
              <span
                className={
                  "flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold " +
                  stageClassName[stage]
                }
              >
                {stage}
              </span>
              {label}
            </span>
          ))}
        </div>
        <span className="flex items-center gap-2 text-[#6687FF]">
          <FiInfo />
          Amount based workflows are evaluated from highest priority to lowest.
        </span>
      </div>
    </section>
  );
}

type MatrixAmountRangeMode = "Up to" | "Between" | "Greater than";

const matrixAmountRangeModes: MatrixAmountRangeMode[] = [
  "Up to",
  "Between",
  "Greater than",
];

const cleanMatrixAmountValue = (value: string) =>
  value.replace(/[^\d,]/g, "");

const parseMatrixAmountRange = (value: string) => {
  const text = value.trim();
  const amounts = text.match(/[\d,]+/g) || [];
  if (text.includes("-")) {
    return {
      mode: "Between" as MatrixAmountRangeMode,
      first: amounts[0] || "",
      second: amounts[1] || "",
    };
  }
  if (text.includes(">")) {
    return {
      mode: "Greater than" as MatrixAmountRangeMode,
      first: amounts[0] || "",
      second: "",
    };
  }
  return {
    mode: "Up to" as MatrixAmountRangeMode,
    first: amounts[0] || "",
    second: "",
  };
};

const formatMatrixAmountRange = (
  mode: MatrixAmountRangeMode,
  first: string,
  second = "",
) => {
  if (mode === "Between") {
    return `\u20B9${first} - \u20B9${second}`;
  }
  if (mode === "Greater than") {
    return `> \u20B9${first}`;
  }
  return `\u2264 \u20B9${first}`;
};

const isMatrixAmountRangeComplete = (value: string) => {
  const parsed = parseMatrixAmountRange(value);
  return Boolean(
    cleanMatrixAmountValue(parsed.first || "").trim() &&
      (parsed.mode !== "Between" ||
        cleanMatrixAmountValue(parsed.second || "").trim()),
  );
};

function MatrixAmountInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid h-10 grid-cols-[42px_minmax(0,1fr)] overflow-hidden rounded-[6px] border border-[#E0E5F2] bg-white focus-within:border-[#6687FF]">
      <span className="flex items-center justify-center border-r border-[#E0E5F2] text-sm font-semibold text-[#505E8E]">
        {"\u20B9"}
      </span>
      <input
        value={value}
        onChange={(event) => onChange(cleanMatrixAmountValue(event.target.value))}
        className="min-w-0 px-3 text-sm font-semibold text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
        placeholder="10,000"
        inputMode="numeric"
      />
    </div>
  );
}

function MatrixAmountRangeInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const parsed = parseMatrixAmountRange(value);
  const update = (
    mode: MatrixAmountRangeMode,
    first: string,
    second = parsed.second,
  ) => {
    onChange(formatMatrixAmountRange(mode, first, second));
  };

  return (
    <div>
      <FieldLabel required>Amount Range</FieldLabel>
      <div
        className={
          "grid items-center gap-3 " +
          (parsed.mode === "Between"
            ? "sm:grid-cols-[170px_minmax(0,1fr)_auto_minmax(0,1fr)]"
            : "sm:grid-cols-[170px_minmax(0,1fr)]")
        }
      >
        <PayoutDropdown
          value={parsed.mode}
          options={matrixAmountRangeModes}
          onChange={(mode) =>
            update(
              mode as MatrixAmountRangeMode,
              parsed.first || "",
              parsed.second || "",
            )
          }
          className="min-w-0"
          buttonClassName="h-10"
        />
        <MatrixAmountInput
          value={parsed.first || ""}
          onChange={(amount) => update(parsed.mode, amount, parsed.second || "")}
        />
        {parsed.mode === "Between" && (
          <>
            <span className="text-center text-sm font-semibold text-[#505E8E]">
              and
            </span>
            <MatrixAmountInput
              value={parsed.second || ""}
              onChange={(amount) => update(parsed.mode, parsed.first || "", amount)}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ConfigureRuleMatrix({
  matrixItems,
  onSave,
}: {
  matrixItems: RuleMatrixRow[];
  onSave: (rows: RuleMatrixRow[], stepUpToken?: string) => Promise<void> | void;
}) {
  const [draftRows, setDraftRows] = React.useState<RuleMatrixRow[]>(matrixItems);
  const [rowKeys, setRowKeys] = React.useState<string[]>(() =>
    matrixItems.map((_, index) => `matrix-row-${index}-${Date.now()}`),
  );
  const [message, setMessage] = React.useState("");
  const [pendingRows, setPendingRows] = React.useState<RuleMatrixRow[]>([]);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [verificationMethod, setVerificationMethod] = React.useState<
    "authenticator" | "sms"
  >("authenticator");
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill(""),
  );

  React.useEffect(() => {
    setDraftRows(matrixItems);
    setRowKeys((current) =>
      matrixItems.map(
        (_, index) => current[index] ?? `matrix-row-${index}-${Date.now()}`,
      ),
    );
  }, [matrixItems]);

  const updateRow = (
    index: number,
    key: keyof RuleMatrixRow,
    value: string | string[],
  ) => {
    setDraftRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    );
  };

  const handleAddRow = () => {
    setRowKeys((current) => [
      ...current,
      `matrix-row-${current.length}-${Date.now()}`,
    ]);
    setDraftRows((current) => [
      ...current,
      {
        amount: "\u2264 \u20B90",
        condition: "Describe condition",
        workflow: ["M", "A"],
        priority: "Medium",
        status: "Active",
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setRowKeys((current) => current.filter((_, rowIndex) => rowIndex !== index));
    setDraftRows((current) =>
      current.filter((_, rowIndex) => rowIndex !== index),
    );
  };

  const handleSave = () => {
    const hasMissingValue = draftRows.some(
      (row) =>
        !isMatrixAmountRangeComplete(row.amount) ||
        !row.condition.trim() ||
        row.workflow.length === 0,
    );
    if (hasMissingValue) {
      setMessage("Please fill every amount range, condition and workflow before saving.");
      return;
    }

    setMessage("");

    if (!isPayoutStepUpEnabled()) {
      void (async () => {
        try {
          setIsSubmitting(true);
          await onSave(draftRows);
          setIsSuccess(true);
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error) {
          setMessage(errorMessage(error, "Unable to save workflow matrix changes."));
        } finally {
          setIsSubmitting(false);
        }
      })();
      return;
    }

    setPendingRows(draftRows);
    setVerificationCode(Array(6).fill(""));
    setIsVerifying(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setVerificationCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
  };

  const handleVerifyAndSave = async () => {
    const otpError = validateOtpCode(
      verificationCode.join(""),
      "Enter the 6-digit verification code before saving changes.",
    );
    if (otpError) {
      setMessage(otpError);
      return;
    }

    try {
      setIsSubmitting(true);
      const stepUpToken =
        isPayoutApiMode() && isPayoutStepUpEnabled()
          ? await verifyStepUp({
              purpose: "UPDATE_APPROVAL_RULE_MATRIX",
              method: verificationMethod,
              code: verificationCode.join(""),
              subjectId: "approval-rule-matrix",
            })
          : undefined;
      await onSave(pendingRows.length ? pendingRows : draftRows, stepUpToken);
      setMessage("");
      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setMessage(errorMessage(error, "Unable to save workflow matrix changes."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-8 text-center new-shadow">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF2EE] text-2xl font-semibold text-[#2B6B47]">
          {"\u2713"}
        </span>
        <h2 className="mt-6 text-[24px] font-semibold text-[#1B163B]">
          Workflow Matrix Updated
        </h2>
        <p className="mx-auto mt-2 max-w-[560px] text-sm font-medium text-[#505E8E]">
          Your workflow matrix changes have been verified and saved successfully.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/payouts/approval-rules/rule-matrix"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
          >
            Back to Workflow Matrix
          </Link>
        </div>
      </section>
    );
  }

  if (isVerifying) {
    return (
      <section className="mx-auto max-w-[920px] rounded-[8px] border border-[#E0E5F2] bg-white p-8 new-shadow">
        <h2 className="text-[22px] font-semibold text-[#1B163B]">
          Verify Workflow Matrix Changes
        </h2>
        <p className="mt-2 text-sm font-medium text-[#505E8E]">
          Enter the Google Authenticator code before saving changes.
        </p>

        <div className="mt-6">
          {[
            ["authenticator", "Google Authenticator", "Use the code from your authenticator app"],
          ].map(([method, title, helper]) => (
            <button
              key={method}
              type="button"
              onClick={() => setVerificationMethod("authenticator")}
              className={
                "flex min-h-[92px] items-center justify-between rounded-[8px] border px-5 text-left " +
                (verificationMethod === method
                  ? "border-[#1B163B] bg-white"
                  : "border-[#E0E5F2] bg-white")
              }
            >
              <span>
                <span className="block font-semibold text-[#1B163B]">{title}</span>
                <span className="mt-1 block text-sm font-medium text-[#505E8E]">
                  {helper}
                </span>
              </span>
              {verificationMethod === method && (
                <span className="text-[#2B6B47]">{"\u2713"}</span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-7">
          <FieldLabel>Verification Code</FieldLabel>
          <div className="flex flex-wrap gap-3">
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                value={digit}
                onChange={(event) => handleCodeChange(index, event.target.value)}
                className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                maxLength={1}
                inputMode="numeric"
              />
            ))}
          </div>
          {message && (
            <p className="mt-4 rounded-[6px] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
              {message}
            </p>
          )}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => {
              setIsVerifying(false);
              setMessage("");
            }}
            className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleVerifyAndSave}
            disabled={isSubmitting}
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="h-10 rounded-[6px] px-7 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Saving..." : "Verify & Save Changes"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-5">
      <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Matrix Rows
            </h2>
            <p className="mt-1 text-sm font-medium text-[#505E8E]">
              Define amount ranges, conditions and approval paths your team will follow.
            </p>
            <p className="mt-3 flex items-center gap-2 text-xs font-medium text-[#505E8E]">
              <FiInfo className="text-[#6687FF]" />
              Use structured ranges to avoid overlap and parsing errors.
            </p>
          </div>
          <button
            type="button"
            onClick={handleAddRow}
            className="flex h-10 min-w-[132px] shrink-0 items-center justify-center gap-2 rounded-[6px] bg-[#6687FF] px-4 text-sm font-semibold text-white shadow-sm"
          >
            <FiPlus />
            Add Row
          </button>
        </div>

        {message && (
          <p className="mb-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
            {message}
          </p>
        )}

        <div className="space-y-4">
          {draftRows.map((row, index) => (
            <div
              key={rowKeys[index] ?? `matrix-row-${index}`}
              className="rounded-[8px] border border-[#E0E5F2] bg-white p-4"
            >
              <div className="mb-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#6687FF1A] text-sm font-semibold text-[#6687FF]">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-[#1B163B]">
                      Workflow Matrix Row
                    </p>
                    <p className="text-xs font-medium text-[#505E8E]">
                      {row.workflow.join(" -> ")}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveRow(index)}
                  disabled={draftRows.length === 1}
                  className="flex h-9 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-semibold text-[#A04444] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FiTrash2 />
                  Remove
                </button>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <MatrixAmountRangeInput
                  value={row.amount}
                  onChange={(value) => updateRow(index, "amount", value)}
                />
                <label className="hidden">
                  <FieldLabel required>Amount Range</FieldLabel>
                  <input
                    value={row.amount}
                    onChange={(event) => updateRow(index, "amount", event.target.value)}
                    className="h-10 w-full rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-semibold text-[#1B163B] outline-none focus:border-[#6687FF]"
                    placeholder="Enter amount range"
                  />
                </label>
                <label className="block">
                  <FieldLabel required>Condition</FieldLabel>
                  <input
                    value={row.condition}
                    onChange={(event) =>
                      updateRow(index, "condition", event.target.value)
                    }
                    className="h-10 w-full rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-semibold text-[#1B163B] outline-none focus:border-[#6687FF]"
                    placeholder="Enter condition"
                  />
                </label>
                <PayoutDropdown
                  label="Approval Workflow"
                  value={row.workflow.join(" -> ")}
                  options={workflowPathOptions}
                  onChange={(value) =>
                    updateRow(index, "workflow", workflowPathToStages(value))
                  }
                  className="min-w-0"
                />
                <PayoutDropdown
                  label="Priority"
                  value={row.priority}
                  options={priorityLabels}
                  onChange={(value) => updateRow(index, "priority", value)}
                  className="min-w-0"
                />
                <PayoutDropdown
                  label="Status"
                  value={row.status}
                  options={["Active", "Inactive"]}
                  onChange={(value) => updateRow(index, "status", value)}
                  className="min-w-0"
                />
                <div>
                  <FieldLabel>Preview</FieldLabel>
                  <div className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-[#F8FAFD] px-3">
                    <StagePath stages={row.workflow} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="flex items-center justify-between gap-4 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <Link
          to="/payouts/approval-rules/rule-matrix"
          className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
        >
          Cancel
        </Link>
        <button
          type="button"
          onClick={handleSave}
          style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
          className="flex h-10 items-center gap-3 rounded-[6px] px-6 text-sm font-semibold text-white"
        >
          Save Changes
          <FaArrowRight className="text-xs" />
        </button>
      </section>
    </div>
  );
}

function FieldLabel({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span className="mb-2 block text-xs font-semibold text-[#505E8E]">
      {children}
      {required && <span className="text-[#EF4444]"> *</span>}
    </span>
  );
}

function TextInput({
  label,
  placeholder,
  required = false,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  required?: boolean;
  value?: string;
  onChange?: (value: string) => void;
}) {
  return (
    <label className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <input
        className="h-11 w-full rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
      />
    </label>
  );
}

function SelectInput({
  label,
  value,
  required = false,
  options,
  onChange,
}: {
  label: string;
  value: string;
  required?: boolean;
  options?: string[];
  onChange?: (value: string) => void;
}) {
  return (
    <div className="block">
      <FieldLabel required={required}>{label}</FieldLabel>
      <PayoutDropdown
        value={value}
        options={options}
        onChange={onChange}
        className="min-w-0"
        buttonClassName="h-11"
      />
    </div>
  );
}

function RadioDot({ active = false }: { active?: boolean }) {
  return (
    <span
      className={
        "flex h-5 w-5 items-center justify-center rounded-full border " +
        (active ? "border-[#6687FF]" : "border-[#A3AED0]")
      }
    >
      {active && <span className="h-2.5 w-2.5 rounded-full bg-[#6687FF]" />}
    </span>
  );
}

function NumberBadge({ value }: { value: number }) {
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full border border-[#6687FF] text-xs font-semibold text-[#6687FF]">
      {value}
    </span>
  );
}

type WorkflowFormStep = "details" | "verify" | "confirm";

function NewRule({
  approvalGroups,
  onSave,
  onStepChange,
  initialRule,
  mode = "create",
}: {
  approvalGroups: ApprovalGroup[];
  onSave: (rule: ApprovalRule, stepUpToken?: string) => Promise<void> | void;
  onStepChange?: (step: WorkflowFormStep) => void;
  initialRule?: ApprovalRule;
  mode?: "create" | "edit";
}) {
  const isEditMode = mode === "edit";
  const stepUpRequired = isPayoutStepUpEnabled();
  const parsedInitialCondition = parseAmountCondition(initialRule?.condition ?? "");
  const initialApprovalGroup =
    approvalGroups.find((group) => group.id === initialRule?.approvalGroupId) ??
    approvalGroups.find(
      (group) =>
        initialRule?.approvalGroupName &&
        group.name.trim().toLowerCase() === initialRule.approvalGroupName.trim().toLowerCase(),
    ) ??
    approvalGroups.find(
      (group) =>
        group.flow
          .split(" -> ")
          .map((stage) => stage[0])
          .join("") === (initialRule?.workflow ?? []).join(""),
    ) ?? approvalGroups[0];
  const fallbackApprovalGroup: ApprovalGroup = {
    id: "",
    name: "-",
    description: "",
    levels: "0 Levels",
    flow: "Maker",
    users: [],
    levelDetails: [],
    createdAt: "-",
    createdBy: "-",
    lastUpdated: "-",
    status: "Active",
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  };
  const [ruleName, setRuleName] = React.useState(initialRule?.name ?? "");
  const [appliesTo, setAppliesTo] = React.useState(initialRule?.appliesTo ?? "Transfers");
  const [priority, setPriority] = React.useState(
    normalizePriority(initialRule?.priority ?? "High"),
  );
  const [description, setDescription] = React.useState("");
  const [operator, setOperator] = React.useState(parsedInitialCondition.operator);
  const [amount, setAmount] = React.useState(parsedInitialCondition.amount);
  const [additionalConditions, setAdditionalConditions] = React.useState<
    { id: string; operator: string; amount: string }[]
  >([]);
  const [message, setMessage] = React.useState("");
  const [selectedApprovalGroupId, setSelectedApprovalGroupId] = React.useState(() => {
    if (initialRule?.stageGroupIds) {
      const ids = Object.values(initialRule.stageGroupIds).filter(Boolean) as string[];
      const uniqueIds = [...new Set(ids)];
      if (uniqueIds.length === 1) return uniqueIds[0];
    }
    return initialApprovalGroup?.id ?? initialRule?.approvalGroupId ?? "";
  });
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [verificationMethod, setVerificationMethod] = React.useState<
    "authenticator" | "sms"
  >("authenticator");
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill(""),
  );
  const [pendingRule, setPendingRule] = React.useState<ApprovalRule | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const activeApprovalGroups = approvalGroups.filter((group) => group.status !== "Inactive");
  const resolveGroup = (id?: string) =>
    approvalGroups.find((group) => group.id === id) || fallbackApprovalGroup;
  const deriveWorkflowStageCodes = (group: ApprovalGroup): ("M" | "C" | "A" | "U")[] => {
    const levelOrder = { M: 0, C: 1, A: 2, U: 3 };
    const levels = group.levelDetails?.length
      ? group.levelDetails
      : group.apiStage || group.memberUserIds?.length
        ? [
            {
              stage:
                group.apiStage === "MAKER"
                  ? ("M" as const)
                  : group.apiStage === "APPROVER"
                    ? ("A" as const)
                    : group.apiStage === "AUDITOR"
                      ? ("U" as const)
                      : ("C" as const),
              title: group.flow.split(" -> ")[0] || "Checker",
              users: group.memberUserIds ?? [],
              mode: "Any One Approves" as const,
            },
          ]
        : group.flow.split(" -> ").map((title) => ({
            stage: (title.trim() === "Maker"
              ? "M"
              : title.trim() === "Approver"
                ? "A"
                : title.trim() === "Auditor"
                  ? "U"
                  : "C") as "M" | "C" | "A" | "U",
            title: title.trim(),
            users: [] as string[],
            mode: "Any One Approves" as const,
          }));
    const filled = levels
      .filter((level) => level.users.length > 0)
      .sort((first, second) => levelOrder[first.stage] - levelOrder[second.stage]);
    if (filled.length) {
      return filled.map((level) => level.stage);
    }
    if (group.apiStage === "CHECKER") return ["M", "C"];
    if (group.apiStage === "APPROVER") return ["M", "A"];
    if (group.apiStage === "AUDITOR") return ["M", "U"];
    if (group.apiStage === "MAKER") return ["M"];
    return ["M"];
  };
  const selectedApprovalGroup = resolveGroup(selectedApprovalGroupId);
  const workflowStageCodes = selectedApprovalGroupId
    ? deriveWorkflowStageCodes(selectedApprovalGroup)
    : [];
  const workflowPath = workflowStageCodes.join(" -> ");
  const stageGroupIds = selectedApprovalGroupId
    ? Object.fromEntries(workflowStageCodes.map((code) => [code, selectedApprovalGroupId]))
    : {};
  const workflowLevelMemberCount = (code: "M" | "C" | "A" | "U") => {
    const level = (selectedApprovalGroup.levelDetails ?? []).find((item) => item.stage === code);
    if (level?.users.length) return level.users.length;
    if (
      selectedApprovalGroup.memberUserIds?.length &&
      workflowStageCodes.length === 1 &&
      workflowStageCodes[0] === code
    ) {
      return selectedApprovalGroup.memberUserIds.length;
    }
    return 0;
  };
  const formatConditionText = (conditionOperator: string, conditionAmount: string) =>
    conditionOperator === "Greater Than (>)"
      ? `Amount > \u20B9${conditionAmount}`
      : conditionOperator === "Greater Than or Equal (>=)"
        ? `Amount >= \u20B9${conditionAmount}`
        : `Amount <= \u20B9${conditionAmount}`;

  const conditionText = [
    formatConditionText(operator, amount),
    ...additionalConditions.map((condition) =>
      formatConditionText(condition.operator, condition.amount),
    ),
  ].join(" AND ");

  React.useEffect(() => {
    onStepChange?.(isSuccess ? "confirm" : isVerifying ? "verify" : "details");
  }, [isSuccess, isVerifying, onStepChange]);

  const handleUpdateAdditionalCondition = (
    id: string,
    field: "operator" | "amount",
    value: string,
  ) => {
    setAdditionalConditions((current) =>
      current.map((condition) =>
        condition.id === id ? { ...condition, [field]: value } : condition,
      ),
    );
  };

  const handleRemoveAdditionalCondition = (id: string) => {
    setAdditionalConditions((current) =>
      current.filter((condition) => condition.id !== id),
    );
  };

  const buildRule = (): ApprovalRule | null => {
    if (!ruleName.trim()) {
      setMessage("Please add a workflow name before saving.");
      return null;
    }

    const normalizedAmount = normalizeAmountInput(amount);
    const amountError = validatePositiveAmountInput(
      amount,
      "Please add a valid amount condition before saving.",
    );
    if (amountError) {
      setMessage(amountError);
      return null;
    }

    const invalidAdditionalCondition = additionalConditions.some((condition) => {
      return Boolean(
        validatePositiveAmountInput(
          condition.amount,
          "Please add a valid amount for every added condition.",
        ),
      );
    });
    if (invalidAdditionalCondition) {
      setMessage("Please add a valid amount for every added condition.");
      return null;
    }

    const workflowStages = workflowStageCodes;
    if (!selectedApprovalGroupId) {
      setMessage("Select an approval group for this workflow.");
      return null;
    }
    if (!workflowStages.length) {
      setMessage("Selected approval group has no members at any stage.");
      return null;
    }
    const nextStageGroupIds = Object.fromEntries(
      workflowStages.map((code) => [code, selectedApprovalGroupId]),
    ) as { M?: string; C?: string; A?: string; U?: string };
    const amountPaise = rupeesToPaise(Number(normalizedAmount));
    let amountMinPaise = 1;
    let amountMaxPaise: number | null = null;
    if (operator.includes("<=")) {
      amountMinPaise = 1;
      amountMaxPaise = amountPaise;
    } else if (operator.includes(">=")) {
      amountMinPaise = amountPaise;
      amountMaxPaise = null;
    } else {
      amountMinPaise = amountPaise + 1;
      amountMaxPaise = null;
    }

    return {
      name: ruleName.trim(),
      appliesTo,
      type: "Amount Based",
      condition: conditionText,
      workflow: workflowStages,
      priority,
      status: initialRule?.status ?? "Active",
      workflowId: initialRule?.workflowId,
      approvalGroupId:
        nextStageGroupIds.M ||
        nextStageGroupIds.C ||
        nextStageGroupIds.A ||
        nextStageGroupIds.U ||
        "",
      approvalGroupName: selectedApprovalGroup.name,
      stageGroupIds: nextStageGroupIds,
      amountMinPaise,
      amountMaxPaise,
      icon: initialRule?.icon ?? <LuRefreshCw />,
      tone: initialRule?.tone ?? "bg-[#6687FF1A] text-[#6687FF]",
    };
  };

  const handleVerificationCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setVerificationCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
  };

  const handleVerificationKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !verificationCode[index] && index > 0) {
      const previous = document.getElementById(
        `approval-rule-otp-${index - 1}`,
      ) as HTMLInputElement | null;
      previous?.focus();
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isVerifying) {
      const nextRule = buildRule();
      if (!nextRule) return;

      setPendingRule(nextRule);
      setMessage("");
      setVerificationCode(Array(6).fill(""));
      setIsVerifying(true);
      onStepChange?.("verify");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const otpError = validateOtpCode(
      verificationCode.join(""),
      "Enter the 6-digit verification code before saving this workflow.",
    );
    if (otpError) {
      setMessage(otpError);
      return;
    }

    const savedRule = pendingRule ?? buildRule()!;
    try {
      setIsSubmitting(true);
      const stepUpToken =
        isPayoutApiMode() && stepUpRequired
          ? await verifyStepUp({
              purpose: isEditMode
                ? "UPDATE_APPROVAL_WORKFLOW"
                : "CREATE_APPROVAL_WORKFLOW",
              method: verificationMethod,
              code: verificationCode.join(""),
              subjectId: savedRule.name,
            })
          : undefined;
      await onSave(savedRule, stepUpToken);
      setPendingRule(savedRule);
      setIsSuccess(true);
      onStepChange?.("confirm");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setMessage(errorMessage(error, "Unable to save this workflow."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    const savedRule = pendingRule ?? buildRule();
    const savedRuleName = savedRule?.name ?? ruleName;

    return (
      <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-8 text-center new-shadow">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF2EE] text-2xl font-semibold text-[#2B6B47]">
          {"✓"}
        </span>
        <h2 className="mt-6 text-[24px] font-semibold text-[#1B163B]">
          {isEditMode ? "Workflow Updated Successfully" : "Workflow Saved Successfully"}
        </h2>
        <p className="mx-auto mt-2 max-w-[560px] text-sm font-medium text-[#505E8E]">
          {savedRuleName} has been verified and saved. It will now be used for
          matching payout transfer approvals.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/payouts/approval-rules/workflow-rules"
            className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
          >
            <FiArrowLeft />
            Back to Workflows
          </Link>
          <Link
            to={getRuleDetailsPath(savedRule ?? { name: savedRuleName })}
            className="flex h-10 items-center gap-2 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white"
          >
            View Details
            <FiExternalLink />
          </Link>
        </div>
      </section>
    );
  }

  if (isVerifying) {
    const verificationDescription =
      "Enter the 6-digit code from your Google Authenticator app.";

    return (
      <form
        id="new-approval-rule-form"
        onSubmit={handleSubmit}
        className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]"
      >
        <div className="space-y-5">
          {message && (
            <p className="rounded-[6px] border border-[#F59E0B66] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
              {message}
            </p>
          )}
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <div className="mb-6 flex items-start gap-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-[8px] bg-[#F4F7FE] text-xl text-[#1B163B]">
                <LuShield />
              </span>
              <div>
                <h2 className="text-[22px] font-semibold text-[#1B163B]">
                  {isEditMode ? "Verify Workflow Update" : "Verify Workflow Save"}
                </h2>
                <p className="mt-2 text-sm font-medium text-[#505E8E]">
                  {verificationDescription}
                </p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {[
                {
                  key: "authenticator",
                  title: "Google Authenticator",
                  helper: "Recommended for approval workflow changes",
                  icon: <LuShield />,
                },
              ].map((method) => {
                const active = verificationMethod === method.key;
                return (
                  <button
                    key={method.key}
                    type="button"
                    onClick={() => setVerificationMethod("authenticator")}
                    className={
                      "flex min-h-[92px] items-center justify-between rounded-[8px] border px-5 text-left " +
                      (active
                        ? "border-[#1B163B] bg-white"
                        : "border-[#E0E5F2] bg-white")
                    }
                  >
                    <span className="flex items-center gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-[8px] bg-[#F4F7FE] text-lg text-[#6687FF]">
                        {method.icon}
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-[#1B163B]">
                          {method.title}
                        </span>
                        <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                          {method.helper}
                        </span>
                      </span>
                    </span>
                    {active && (
                      <span className="text-lg font-semibold text-[#166534]">✓</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-8">
              <FieldLabel required>Verification Code</FieldLabel>
              <div className="flex flex-wrap gap-3">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`approval-rule-otp-${index}`}
                    value={digit}
                    onChange={(event) => {
                      handleVerificationCodeChange(index, event.target.value);
                      if (event.target.value && index < 5) {
                        const next = document.getElementById(
                          `approval-rule-otp-${index + 1}`,
                        ) as HTMLInputElement | null;
                        next?.focus();
                      }
                    }}
                    onKeyDown={(event) => handleVerificationKeyDown(index, event)}
                    className="h-14 w-14 rounded-[8px] border border-[#E0E5F2] bg-white text-center text-xl font-semibold text-[#1B163B] outline-none focus:border-[#6687FF]"
                    maxLength={1}
                    inputMode="numeric"
                  />
                ))}
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-5">
                <button
                  type="button"
                  onClick={() => setVerificationCode(Array(6).fill(""))}
                  className="text-sm font-semibold text-[#1B163B]"
                >
                  Resend code
                </button>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[#E0E5F2] pt-5">
              <button
                type="button"
                onClick={() => {
                  setIsVerifying(false);
                  onStepChange?.("details");
                  setMessage("");
                }}
                className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
              >
                <FiArrowLeft />
                Back to Edit
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
                className="flex h-10 min-w-[180px] items-center justify-center gap-3 rounded-[6px] px-7 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? "Saving..." : "Verify & Continue"}
                {!isSubmitting && <FaArrowRight className="text-xs" />}
              </button>
            </div>
          </section>
        </div>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
            Verification Summary
          </h2>
          <div className="mb-5 h-px bg-[#E0E5F2]" />
          {[
            ["Workflow Name", pendingRule?.name ?? ruleName],
            ["Condition", pendingRule?.condition ?? conditionText],
            ["Priority", pendingRule?.priority ?? priority],
            ["Approval Path", workflowPath],
            [
              "Groups",
              [
                stageGroupIds.C && `C:${resolveGroup(stageGroupIds.C).name}`,
                stageGroupIds.A && `A:${resolveGroup(stageGroupIds.A).name}`,
                stageGroupIds.U && `U:${resolveGroup(stageGroupIds.U).name}`,
              ]
                .filter(Boolean)
                .join(" · ") || "-",
            ],
            [
              "Verification",
              "Google Authenticator",
            ],
          ].map(([label, value]) => (
            <div key={label} className="mb-4 flex justify-between gap-4 text-sm">
              <span className="font-medium text-[#505E8E]">{label}</span>
              <span className="text-right font-semibold text-[#1B163B]">
                {value}
              </span>
            </div>
          ))}
          <div className="mt-5 rounded-[6px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#505E8E]">
            This verification protects approval workflow changes before they are
            applied.
          </div>
        </aside>
      </form>
    );
  }

  return (
    <form
      id="new-approval-rule-form"
      onSubmit={handleSubmit}
      className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]"
    >
      <div className="space-y-5">
        {message && (
          <p className="rounded-[6px] border border-[#F59E0B66] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
            {message}
          </p>
        )}
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-center gap-4">
            <NumberBadge value={1} />
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
                1. Workflow Information
            </h2>
          </div>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_180px_150px]">
            <TextInput
              label="Workflow Name"
              placeholder="Enter workflow name"
              required
              value={ruleName}
              onChange={setRuleName}
            />
            <SelectInput
              label="Applies To"
              value={appliesTo}
              required
              options={["Transfers"]}
              onChange={setAppliesTo}
            />
            <SelectInput
              label="Priority"
              value={priority}
              required
              options={priorityLabels}
              onChange={setPriority}
            />
          </div>
          <div className="mt-6 max-w-[650px]">
            <TextInput
              label="Description (Optional)"
              placeholder="Enter description"
              value={description}
              onChange={setDescription}
            />
          </div>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-5 flex items-center gap-4">
            <NumberBadge value={2} />
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                2. Workflow Type
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                Define the basis on which this workflow will be applied
              </p>
            </div>
          </div>
          <div className="grid max-w-[320px] gap-4">
            {[
              ["Amount Based", "Based on transfer amount"],
            ].map(([title, helper], index) => (
              <button
                type="button"
                key={title}
                className="flex min-h-[76px] items-start gap-3 rounded-[6px] border border-[#E0E5F2] bg-white p-4 text-left"
              >
                <RadioDot active={index === 0} />
                <span>
                  <span className="block text-sm font-semibold text-[#1B163B]">
                    {title}
                  </span>
                  <span className="mt-2 block text-xs font-medium text-[#505E8E]">
                    {helper}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-5 flex items-center gap-4">
            <NumberBadge value={2} />
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                3. Conditions
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                Set the conditions that must be met for this workflow to apply
              </p>
            </div>
          </div>
          <div className="grid items-end gap-6 lg:grid-cols-[190px_190px_230px_minmax(0,1fr)]">
            <SelectInput label="" value="Amount" options={["Amount"]} />
            <SelectInput
              label=""
              value={operator}
              options={[
                "Greater Than (>)",
                "Greater Than or Equal (>=)",
                "Less Than or Equal (<=)",
              ]}
              onChange={setOperator}
            />
            <label>
              <FieldLabel>Amount</FieldLabel>
              <div className="grid h-11 grid-cols-[44px_minmax(0,1fr)] rounded-[6px] border border-[#E0E5F2] bg-white">
                <span className="flex items-center justify-center border-r border-[#E0E5F2] text-sm font-semibold text-[#1B163B]">
                  {"\u20B9"}
                </span>
                <input
                  className="px-3 text-sm font-semibold text-[#1B163B] outline-none"
                  value={amount}
                  onChange={(event) =>
                    setAmount(event.target.value.replace(/[^\d,]/g, ""))
                  }
                />
              </div>
            </label>
          </div>
          {additionalConditions.length > 0 && (
            <div className="mt-4 space-y-4">
              {additionalConditions.map((condition, index) => (
                <div
                  key={condition.id}
                  className="grid items-end gap-6 rounded-[8px] border border-[#E0E5F2] bg-[#F8FAFD] p-4 lg:grid-cols-[190px_190px_230px_auto]"
                >
                  <SelectInput label="" value="Amount" options={["Amount"]} />
                  <SelectInput
                    label=""
                    value={condition.operator}
                    options={[
                      "Greater Than (>)",
                      "Greater Than or Equal (>=)",
                      "Less Than or Equal (<=)",
                    ]}
                    onChange={(value) =>
                      handleUpdateAdditionalCondition(
                        condition.id,
                        "operator",
                        value,
                      )
                    }
                  />
                  <label>
                    <FieldLabel>Amount</FieldLabel>
                    <div className="grid h-11 grid-cols-[44px_minmax(0,1fr)] rounded-[6px] border border-[#E0E5F2] bg-white">
                      <span className="flex items-center justify-center border-r border-[#E0E5F2] text-sm font-semibold text-[#1B163B]">
                        {"\u20B9"}
                      </span>
                      <input
                        className="px-3 text-sm font-semibold text-[#1B163B] outline-none"
                        placeholder={`Condition ${index + 2} amount`}
                        value={condition.amount}
                        onChange={(event) =>
                          handleUpdateAdditionalCondition(
                            condition.id,
                            "amount",
                            event.target.value.replace(/[^\d,]/g, ""),
                          )
                        }
                      />
                    </div>
                  </label>
                  <button
                    type="button"
                    onClick={() => handleRemoveAdditionalCondition(condition.id)}
                    className="flex h-11 items-center justify-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
                  >
                    <FiTrash2 />
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-5 flex items-center gap-3 rounded-[6px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#505E8E]">
            <FiInfo className="text-[#6687FF]" />
            All conditions within a workflow must be satisfied (AND logic).
          </div>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-5 flex items-center gap-4">
            <NumberBadge value={4} />
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                4. Approval Group
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                Select an approval group — the workflow path is detected from the stages and
                members defined in that group.
              </p>
            </div>
          </div>
          <div className="max-w-[560px] space-y-5">
            <PayoutDropdown
              label="Approval Group"
              value={
                selectedApprovalGroupId
                  ? selectedApprovalGroup.name
                  : "Select approval group"
              }
              options={
                activeApprovalGroups.length
                  ? activeApprovalGroups.map((group) => group.name)
                  : ["No active approval groups"]
              }
              onChange={(name) => {
                const group = activeApprovalGroups.find((item) => item.name === name);
                if (!group) return;
                setSelectedApprovalGroupId(group.id);
              }}
            />
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <div className="rounded-[6px] border border-[#E0E5F2] bg-white p-4">
              <p className="text-xs font-semibold text-[#505E8E]">Detected workflow path</p>
              <div className="mt-3">
                {workflowStageCodes.length ? (
                  <StagePath stages={workflowStageCodes} />
                ) : (
                  <p className="text-sm font-medium text-[#505E8E]">
                    Select a group to preview its path
                  </p>
                )}
              </div>
              {workflowPath && (
                <p className="mt-2 text-sm font-semibold text-[#1B163B]">{workflowPath}</p>
              )}
            </div>
            <div className="rounded-[6px] border border-[#E0E5F2] bg-white p-4">
              <p className="text-xs font-semibold text-[#505E8E]">Group summary</p>
              <p className="mt-2 text-sm font-semibold text-[#1B163B]">
                {[
                  false
                    ? ""
                    : null,
                  false
                    ? ""
                    : null,
                  selectedApprovalGroupId
                    ? selectedApprovalGroup.name
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "None selected"}
              </p>
              {selectedApprovalGroupId && (
                <p className="mt-1 text-xs font-medium text-[#505E8E]">
                  {selectedApprovalGroup.levels || `${workflowStageCodes.length} stages`}
                  {selectedApprovalGroup.flow ? ` · ${selectedApprovalGroup.flow}` : ""}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <WorkflowPreview
              steps={[
                {
                  stage: "M",
                  title: "Maker",
                  users: "Users with Initiator role",
                  helper: "Creates payout request",
                },
                ...(workflowStageCodes.includes("C")
                  ? [
                      {
                        stage: "C",
                        title: "Checker",
                        users: selectedApprovalGroupId
                          ? `${selectedApprovalGroup.name} (${workflowLevelMemberCount("C")} members)`
                          : "Select an approval group",
                        helper:
                          selectedApprovalGroup.levelDetails?.find((level) => level.stage === "C")?.mode ||
                          "Any one approves",
                      },
                    ]
                  : []),
                ...(workflowStageCodes.includes("A")
                  ? [
                      {
                        stage: "A",
                        title: "Approver",
                        users: selectedApprovalGroupId
                          ? `${selectedApprovalGroup.name} (${workflowLevelMemberCount("A")} members)`
                          : "Select an approval group",
                        helper:
                          selectedApprovalGroup.levelDetails?.find((level) => level.stage === "A")?.mode ||
                          "Any one approves",
                      },
                    ]
                  : []),
                ...(workflowStageCodes.includes("U")
                  ? [
                      {
                        stage: "U",
                        title: "Auditor",
                        users: selectedApprovalGroupId
                          ? `${selectedApprovalGroup.name} (${workflowLevelMemberCount("U")} members)`
                          : "Select an approval group",
                        helper: "Optional audit stage",
                      },
                    ]
                  : []),
                {
                  stage: "✓",
                  title: "Payout Approved",
                  users: "",
                  helper: "Request will be processed",
                },
              ]}
            />
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-[6px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#505E8E]">
            <FiInfo className="text-[#6687FF]" />
            Use distinct stage-filtered groups. Makers enroll as Initiators under Payouts → Users.
          </div>
        </section>
      </div>

      <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
          Workflow Summary
        </h2>
        <div className="mb-5 h-px bg-[#E0E5F2]" />
        <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
          Workflow Information
        </h3>
        {[
          [
            "Workflow Name",
            ruleName || (isEditMode ? initialRule?.name ?? "-" : "-"),
          ],
          ["Applies To", appliesTo],
          ["Priority", priority],
        ].map(([label, value]) => (
          <div key={label} className="mb-4 flex justify-between text-sm">
            <span className="font-medium text-[#505E8E]">{label}</span>
            <span className="font-semibold text-[#1B163B]">{value}</span>
          </div>
        ))}
        <div className="my-5 h-px bg-[#E0E5F2]" />
        <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">Workflow Type</h3>
        <p className="text-sm font-medium text-[#505E8E]">Amount Based</p>
        <div className="my-5 h-px bg-[#E0E5F2]" />
        <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">Conditions</h3>
        <p className="text-sm font-medium text-[#505E8E]">
          {"\u2022"} {conditionText}
        </p>
        <div className="my-5 h-px bg-[#E0E5F2]" />
        <h3 className="mb-4 text-sm font-semibold text-[#1B163B]">
          Approval Stages
        </h3>
        <div className="rounded-[6px] border border-[#E0E5F2] bg-white p-4">
          <p className="text-sm font-semibold text-[#1B163B]">
            {workflowPath}
          </p>
          <div className="mt-4">
            <StagePath stages={workflowStageCodes} />
          </div>
          <p className="mt-3 text-xs font-semibold text-[#505E8E]">
            {[
              stageGroupIds.C && `Checker: ${resolveGroup(stageGroupIds.C).name}`,
              stageGroupIds.A && `Approver: ${resolveGroup(stageGroupIds.A).name}`,
              stageGroupIds.U && `Auditor: ${resolveGroup(stageGroupIds.U).name}`,
            ]
              .filter(Boolean)
              .join(" · ") || "No groups selected"}
          </p>
        </div>
        <div className="mt-2 flex gap-3 rounded-[6px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#505E8E]">
          <FiInfo className="mt-1 text-[#6687FF]" />
          <p>
            The approval path is auto-detected from the selected group&apos;s configured stages.
          </p>
        </div>
      </aside>
    </form>
  );
}

function EditRule({
  approvalRules,
  approvalGroups,
  onSave,
  onStepChange,
}: {
  approvalRules: ApprovalRule[];
  approvalGroups: ApprovalGroup[];
  onSave: (rule: ApprovalRule, stepUpToken?: string) => Promise<void> | void;
  onStepChange?: (step: WorkflowFormStep) => void;
}) {
  const { ruleName } = useParams();
  const decodedRuleName = ruleName ? decodeURIComponent(ruleName) : "";
  const rule = findRuleByRouteKey(approvalRules, decodedRuleName);

  if (!rule) {
    return (
      <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <h1 className="text-[22px] font-semibold text-[#1B163B]">
          Workflow not found
        </h1>
        <p className="mt-2 text-sm font-medium text-[#505E8E]">
          The selected workflow was not returned by the backend.
        </p>
        <Link
          to="/payouts/approval-rules/workflow-rules"
          className="mt-5 inline-flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
        >
          <FiArrowLeft />
          Back to Workflows
        </Link>
      </section>
    );
  }

  return (
    <NewRule
      approvalGroups={approvalGroups}
      initialRule={rule as ApprovalRule}
      mode="edit"
      onSave={onSave}
      onStepChange={onStepChange}
    />
  );
}

type ApprovalGroupStatus = "Active" | "Inactive";

type ApprovalGroupLevel = {
  stage: "M" | "C" | "A" | "U";
  title: string;
  users: string[];
  mode: "Create Only" | "Any One Approves" | "All Must Approve" | "Minimum N Approvals";
  minApprovals?: number;
};

type ApprovalGroup = {
  id: string;
  name: string;
  description: string;
  levels: string;
  flow: string;
  users: string[];
  levelDetails?: ApprovalGroupLevel[];
  createdAt: string;
  createdBy: string;
  lastUpdated: string;
  status: ApprovalGroupStatus;
  tone: string;
  memberUserIds?: string[];
  apiStage?: "MAKER" | "CHECKER" | "APPROVER" | "AUDITOR";
  apiLogic?: "ANY_ONE" | "ALL_MUST" | "ALL" | "MIN_N";
};

const initialApprovalGroups: ApprovalGroup[] = [];

const APPROVAL_GROUPS_STORAGE_KEY = "clearn_payout_approval_groups";
const APPROVAL_GROUP_LEVELS_STORAGE_KEY = "clearn_payout_approval_group_levels";

const hydrateApprovalGroups = (): ApprovalGroup[] => {
  if (typeof window === "undefined") return initialApprovalGroups;

  try {
    const stored = window.localStorage.getItem(APPROVAL_GROUPS_STORAGE_KEY);
    if (!stored) return initialApprovalGroups;

    const parsed = JSON.parse(stored) as Partial<ApprovalGroup>[];
    if (!Array.isArray(parsed)) return initialApprovalGroups;

    const hydratedGroups = sanitizeApprovalGroups(parsed).map((group, index) => ({
      ...group,
      id: group.id || `approval-group-${index + 1}`,
      name: group.name || "-",
      description: group.description || "-",
      levels: group.levels || "-",
      flow: group.flow || "-",
      users: group.users || [],
      levelDetails: group.levelDetails,
      createdAt: group.createdAt || "-",
      createdBy: group.createdBy || "-",
      lastUpdated: group.lastUpdated || "-",
      status: group.status || "Active",
      tone: group.tone || "bg-[#6687FF1A] text-[#6687FF]",
    }));

    return hydratedGroups;
  } catch {
    return initialApprovalGroups;
  }
};

const persistApprovalGroups = (nextGroups: ApprovalGroup[]) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    APPROVAL_GROUPS_STORAGE_KEY,
    JSON.stringify(nextGroups),
  );
};

const readApprovalGroupLevelCache = (): Record<string, Partial<ApprovalGroup>> => {
  if (typeof window === "undefined") return {};
  try {
    const stored = window.localStorage.getItem(APPROVAL_GROUP_LEVELS_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry) => {
        const [cacheKey, group] = entry;
        const cacheGroup =
          group && typeof group === "object"
            ? { ...(group as Partial<ApprovalGroup>), name: (group as Partial<ApprovalGroup>).name || cacheKey }
            : { name: cacheKey };
        return !isSeedApprovalGroup(cacheGroup);
      }),
    ) as Record<string, Partial<ApprovalGroup>>;
  } catch {
    return {};
  }
};

const persistApprovalGroupLevelCache = (group: ApprovalGroup) => {
  if (typeof window === "undefined") return;
  const existing = readApprovalGroupLevelCache();
  const payload: Partial<ApprovalGroup> = {
    id: group.id,
    name: group.name,
    levels: group.levels,
    flow: group.flow,
    users: group.users,
    levelDetails: group.levelDetails,
    memberUserIds: group.memberUserIds,
    apiStage: group.apiStage,
    apiLogic: group.apiLogic,
  };
  window.localStorage.setItem(
    APPROVAL_GROUP_LEVELS_STORAGE_KEY,
    JSON.stringify({
      ...existing,
      [group.id]: payload,
      [group.name]: payload,
    }),
  );
};

const purgeSeedApprovalRulesStorage = () => {
  if (typeof window === "undefined") return;

  try {
    const storedRules = window.localStorage.getItem(APPROVAL_RULES_STORAGE_KEY);
    if (storedRules) {
      const parsed = JSON.parse(storedRules);
      if (Array.isArray(parsed)) {
        window.localStorage.setItem(
          APPROVAL_RULES_STORAGE_KEY,
          JSON.stringify(sanitizeApprovalRules(parsed)),
        );
      }
    }
  } catch {
    window.localStorage.removeItem(APPROVAL_RULES_STORAGE_KEY);
  }

  try {
    const storedGroups = window.localStorage.getItem(APPROVAL_GROUPS_STORAGE_KEY);
    if (storedGroups) {
      const parsed = JSON.parse(storedGroups);
      if (Array.isArray(parsed)) {
        window.localStorage.setItem(
          APPROVAL_GROUPS_STORAGE_KEY,
          JSON.stringify(sanitizeApprovalGroups(parsed)),
        );
      }
    }
  } catch {
    window.localStorage.removeItem(APPROVAL_GROUPS_STORAGE_KEY);
  }

  try {
    const storedMatrix = window.localStorage.getItem(RULE_MATRIX_STORAGE_KEY);
    if (storedMatrix) {
      const parsed = JSON.parse(storedMatrix);
      if (Array.isArray(parsed)) {
        window.localStorage.setItem(
          RULE_MATRIX_STORAGE_KEY,
          JSON.stringify(sanitizeRuleMatrixRows(parsed)),
        );
      }
    }
  } catch {
    window.localStorage.removeItem(RULE_MATRIX_STORAGE_KEY);
  }

  try {
    const storedLevelCache = window.localStorage.getItem(APPROVAL_GROUP_LEVELS_STORAGE_KEY);
    if (storedLevelCache) {
      const parsed = JSON.parse(storedLevelCache);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        window.localStorage.setItem(
          APPROVAL_GROUP_LEVELS_STORAGE_KEY,
          JSON.stringify(
            Object.fromEntries(
              Object.entries(parsed).filter(([cacheKey, group]) => {
                const cacheGroup =
                  group && typeof group === "object"
                    ? {
                        ...(group as Partial<ApprovalGroup>),
                        name: (group as Partial<ApprovalGroup>).name || cacheKey,
                      }
                    : { name: cacheKey };
                return !isSeedApprovalGroup(cacheGroup);
              }),
            ),
          ),
        );
      }
    }
  } catch {
    window.localStorage.removeItem(APPROVAL_GROUP_LEVELS_STORAGE_KEY);
  }
};

const mergeApprovalGroupLevelCache = (group: ApprovalGroup): ApprovalGroup => {
  const cached = readApprovalGroupLevelCache()[group.id] || readApprovalGroupLevelCache()[group.name];
  if (!cached?.levelDetails?.length) return group;
  return {
    ...group,
    levels: cached.levels || group.levels,
    flow: cached.flow || group.flow,
    users: cached.users || group.users,
    levelDetails: cached.levelDetails,
    memberUserIds: cached.memberUserIds || group.memberUserIds,
    apiStage: cached.apiStage || group.apiStage,
    apiLogic: cached.apiLogic || group.apiLogic,
  };
};

const dedupeApprovalGroups = (groups: ApprovalGroup[]) => {
  const unique = new Map<string, ApprovalGroup>();
  groups.forEach((group) => {
    const key = group.name.trim().toLowerCase() || group.id;
    const existing = unique.get(key);
    if (!existing || Date.parse(group.lastUpdated) >= Date.parse(existing.lastUpdated)) {
      unique.set(key, group);
    }
  });
  return Array.from(unique.values());
};

const approvalGroupUserOptions: string[] = [];

const defaultLevelUsers: Record<string, string[]> = {
  Maker: [],
  Checker: [],
  Approver: [],
  Auditor: [],
};

const stageCodeByTitle: Record<string, ApprovalGroupLevel["stage"]> = {
  Maker: "M",
  Checker: "C",
  Approver: "A",
  Auditor: "U",
};

const modeByTitle: Record<string, ApprovalGroupLevel["mode"]> = {
  Maker: "Create Only",
  Checker: "Any One Approves",
  Approver: "All Must Approve",
  Auditor: "Any One Approves",
};

const optionalApprovalLevelOrder = ["Maker", "Checker", "Approver", "Auditor"] as const;

const defaultCreateApprovalLevels = (): ApprovalGroupLevel[] => [
  {
    stage: "M",
    title: "Maker",
    users: [],
    mode: "Create Only",
  },
  {
    stage: "C",
    title: "Checker",
    users: [],
    mode: "Any One Approves",
  },
  {
    stage: "A",
    title: "Approver",
    users: [],
    mode: "Any One Approves",
  },
];

const approvalLevelStageByTitle: Record<string, ApprovalGroupLevel["stage"]> = {
  Maker: "M",
  Checker: "C",
  Approver: "A",
  Auditor: "U",
};

const approvalLevelSortOrder: Record<string, number> = {
  Maker: 0,
  Checker: 1,
  Approver: 2,
  Auditor: 3,
};

const createApprovalLevel = (
  title: (typeof optionalApprovalLevelOrder)[number],
  apiMode: boolean,
): ApprovalGroupLevel => ({
  stage: approvalLevelStageByTitle[title],
  title,
  users: apiMode ? [] : defaultLevelUsers[title] || [],
  mode: modeByTitle[title] || "Any One Approves",
});

const sortApprovalLevels = (levels: ApprovalGroupLevel[]) =>
  [...levels].sort(
    (first, second) =>
      (approvalLevelSortOrder[first.title] ?? 99) -
      (approvalLevelSortOrder[second.title] ?? 99),
  );

const nextMissingApprovalLevelTitle = (levels: ApprovalGroupLevel[]) =>
  optionalApprovalLevelOrder.find(
    (title) => !levels.some((level) => level.title === title),
  );

const approvalModeLabel = (level: ApprovalGroupLevel) =>
  level.mode === "Minimum N Approvals" && level.minApprovals
    ? `Minimum ${level.minApprovals} Approvals`
    : level.mode;

const minimumApprovalValidationError = (levels: ApprovalGroupLevel[]) => {
  const invalidLevel = levels.find((level) => {
    if (level.mode !== "Minimum N Approvals") return false;
    const minimum = Number(level.minApprovals || 0);
    return !Number.isInteger(minimum) || minimum < 1 || minimum > level.users.length;
  });
  if (!invalidLevel) return "";
  return `${invalidLevel.title}: enter a valid N between 1 and ${Math.max(1, invalidLevel.users.length)}.`;
};

const getInitials = (name: string) =>
  name
    .replace(/[^a-zA-Z\s]/g, "")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || name.slice(0, 2).toUpperCase();

const getApprovalGroupLevels = (group: ApprovalGroup): ApprovalGroupLevel[] => {
  if (group.levelDetails?.length) return group.levelDetails;
  if (group.apiStage || group.memberUserIds?.length) {
    const stage =
      group.apiStage === "APPROVER" ? "A" : group.apiStage === "AUDITOR" ? "U" : "C";
    const title =
      group.apiStage === "APPROVER"
        ? "Approver"
        : group.apiStage === "AUDITOR"
          ? "Auditor"
          : "Checker";
    return [
      {
        stage,
        title,
        users: group.memberUserIds?.length
          ? group.memberUserIds
          : group.levelDetails?.[0]?.users || [],
        mode:
          group.apiLogic === "ALL_MUST" || group.apiLogic === "ALL"
            ? "All Must Approve"
            : group.levelDetails?.[0]?.mode || "Any One Approves",
      },
    ];
  }
  if (group.levelDetails?.length) return group.levelDetails;

  return group.flow.split(" -> ").map((title) => {
    const normalizedTitle = title.trim();
    return {
      stage: stageCodeByTitle[normalizedTitle] || "U",
      title: normalizedTitle,
      users: defaultLevelUsers[normalizedTitle] || [],
      mode: modeByTitle[normalizedTitle] || "Any One Approves",
    };
  });
};

const summarizeApprovalGroupUsers = (levels: ApprovalGroupLevel[]) => {
  const users = Array.from(new Set(levels.flatMap((level) => level.users)));
  const initials = users.slice(0, 3).map(getInitials);
  return users.length > 3 ? [...initials, `+${users.length - 3}`] : initials;
};

const approvalGroupUserLabel = (user: string) => {
  const trimmed = user.trim();
  if (!trimmed) return "-";
  if (/^user[_-]/i.test(trimmed) || /^usr[_-]/i.test(trimmed)) {
    return trimmed.length > 18 ? `${trimmed.slice(0, 14)}...` : trimmed;
  }
  return trimmed;
};

const approvalGroupUserSummary = (group: ApprovalGroup) => {
  const levelUsers = getApprovalGroupLevels(group).flatMap((level) => level.users);
  const memberUsers = group.memberUserIds || [];
  const users = Array.from(
    new Set([...levelUsers, ...memberUsers, ...group.users].filter(Boolean)),
  );
  return users.map(approvalGroupUserLabel).filter((user) => user !== "-");
};

function ApprovalGroupUsersCell({ group }: { group: ApprovalGroup }) {
  const users = approvalGroupUserSummary(group);
  const visibleUsers = users.slice(0, 2);
  const hiddenCount = Math.max(0, users.length - visibleUsers.length);
  const cellRef = React.useRef<HTMLDivElement | null>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState<{
    left: number;
    bottom: number;
  } | null>(null);

  if (!users.length) {
    return <span className="font-semibold text-[#505E8E]">-</span>;
  }

  const openTooltip = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    const rect = cellRef.current?.getBoundingClientRect();
    if (!rect) return;
    const tooltipWidth = 280;
    setTooltipPosition({
      left: Math.min(
        Math.max(12, rect.left),
        Math.max(12, window.innerWidth - tooltipWidth - 12),
      ),
      bottom: Math.max(12, window.innerHeight - rect.top + 10),
    });
  };

  const closeTooltip = () => {
    closeTimerRef.current = window.setTimeout(() => {
      setTooltipPosition(null);
    }, 120);
  };

  return (
    <div
      ref={cellRef}
      className="relative min-w-[180px] max-w-[260px]"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
    >
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] bg-[#6687FF1A] text-sm text-[#6687FF]">
          <LuUsers />
        </span>
        <span className="font-semibold text-[#1B163B]">
          {users.length} {users.length === 1 ? "User" : "Users"}
        </span>
      </div>
      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1 text-xs font-medium text-[#505E8E]">
        <span className="max-w-[190px] truncate">
          {visibleUsers.join(", ")}
        </span>
        {hiddenCount > 0 && (
          <span className="shrink-0 rounded-[5px] bg-[#F4F7FE] px-1.5 py-0.5 text-[#505E8E]">
            +{hiddenCount}
          </span>
        )}
      </div>
      {tooltipPosition &&
        createPortal(
          <div
            className="fixed z-[3000] w-[280px] rounded-[6px] border border-[#E0E5F2] bg-white p-3 text-left shadow-2xl"
            style={{
              left: tooltipPosition.left,
              bottom: tooltipPosition.bottom,
            }}
            onMouseEnter={openTooltip}
            onMouseLeave={closeTooltip}
          >
            <p className="mb-2 text-xs font-semibold text-[#505E8E]">
              Assigned Users
            </p>
            <div className="max-h-[220px] overflow-y-auto pr-1">
              {users.map((user, index) => (
                <div
                  key={`${user}-${index}`}
                  className="border-b border-[#E0E5F2] py-2 text-xs font-semibold text-[#1B163B] last:border-b-0"
                >
                  {user}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function ApprovalGroupStatusToggle({
  value,
  onChange,
}: {
  value: ApprovalGroupStatus;
  onChange: (value: ApprovalGroupStatus) => void;
}) {
  const options: ApprovalGroupStatus[] = ["Active", "Inactive"];

  return (
    <div>
      <FieldLabel>Status</FieldLabel>
      <div className="grid h-11 grid-cols-2 gap-1 rounded-[6px] border border-[#E0E5F2] bg-white p-1">
        {options.map((option) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              aria-pressed={selected}
              className="rounded-[5px] text-sm font-semibold transition-colors"
              style={{
                backgroundColor: selected ? "#6687FF" : "transparent",
                color: selected ? "#FFFFFF" : "#505E8E",
                boxShadow: selected ? "0 2px 6px rgba(102, 135, 255, 0.24)" : "none",
              }}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ApprovalGroupLevelEditor({
  levels,
  onChange,
  readOnly = false,
  memberUserOptions,
}: {
  levels: ApprovalGroupLevel[];
  onChange?: (levels: ApprovalGroupLevel[]) => void;
  readOnly?: boolean;
  memberUserOptions?: MemberUserOption[];
}) {
  const apiMode = isPayoutApiMode();
  const optionList = memberUserOptions ?? [];
  const labelById = React.useMemo(() => {
    const map = new Map<string, string>();
    optionList.forEach((option) => map.set(option.id, option.label));
    return map;
  }, [optionList]);
  const idByLabel = React.useMemo(() => {
    const map = new Map<string, string>();
    optionList.forEach((option) => map.set(option.label, option.id));
    return map;
  }, [optionList]);
  const displayUser = (userId: string) => labelById.get(userId) || userId;

  const updateLevel = (
    index: number,
    updater: (level: ApprovalGroupLevel) => ApprovalGroupLevel,
  ) => {
    onChange?.(levels.map((level, levelIndex) => (levelIndex === index ? updater(level) : level)));
  };

  const removeUser = (index: number, user: string) => {
    updateLevel(index, (level) => ({
      ...level,
      users: level.users.filter((item) => item !== user),
    }));
  };

  const addUser = (index: number, user: string) => {
    updateLevel(index, (level) => ({
      ...level,
      users: level.users.includes(user) ? level.users : [...level.users, user],
    }));
  };

  const removeLevel = (index: number) => {
    onChange?.(levels.filter((_, levelIndex) => levelIndex !== index));
  };

  return (
    <div className="space-y-5">
      {apiMode && !optionList.length && (
        <p className="rounded-[6px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm font-medium text-[#92400E]">
          Add users under Payouts → Users first.
        </p>
      )}
      {levels.map((level, index) => (
        <div
          key={`${level.title}-${index}`}
          className="relative rounded-[8px] border border-[#E0E5F2] bg-white"
        >
          <div className="flex items-center gap-5 border-b border-[#E0E5F2] px-5 py-4">
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold " +
                stageClassName[level.stage]
              }
            >
              {index + 1}
            </span>
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-full text-lg " +
                stageClassName[level.stage]
              }
            >
              <LuUsers />
            </span>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h3 className="text-[18px] font-semibold text-[#1B163B]">
                  {level.title}
                </h3>
                {level.stage === "M" ? (
                  <span className="rounded-full bg-[#DCFCE7] px-3 py-1 text-xs font-semibold text-[#16A34A]">
                    Required
                  </span>
                ) : (
                  <span className="rounded-full bg-[#F4F7FE] px-3 py-1 text-xs font-semibold text-[#505E8E]">
                    Optional
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs font-medium text-[#505E8E]">
                {level.stage === "M" ? "Creates the request" : approvalModeLabel(level)}
              </p>
            </div>
            {!readOnly && levels.length > 1 && level.stage !== "M" && (
              <button
                type="button"
                onClick={() => removeLevel(index)}
                className="flex h-9 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-xs font-semibold text-[#505E8E] hover:border-[#EF4444] hover:text-[#EF4444]"
                aria-label={`Remove ${level.title} stage`}
              >
                <FiTrash2 />
                Remove
              </button>
            )}
          </div>

          <div className="grid gap-6 px-12 py-5 lg:grid-cols-2">
            <div>
              <FieldLabel required={level.stage !== "M"}>
                {level.stage === "M" ? "Maker Source" : "Assign Users"}
              </FieldLabel>
              {level.stage === "M" ? (
                <div className="flex min-h-[44px] items-center rounded-[6px] border border-[#E0E5F2] bg-[#F4F7FE] px-3 text-sm font-semibold text-[#1B163B]">
                  Users with Initiator role
                </div>
              ) : (
              <div className="min-h-[44px] rounded-[6px] border border-[#E0E5F2] bg-white px-3 py-2">
                <div className="flex flex-wrap gap-2">
                  {level.users.length ? (
                    level.users.map((user) => (
                      <span
                        key={user}
                        className="inline-flex items-center gap-2 rounded-[5px] bg-[#F4F7FE] px-2.5 py-1.5 text-xs font-semibold text-[#1B163B]"
                      >
                        {displayUser(user)}
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => removeUser(index, user)}
                            className="text-[#505E8E]"
                            aria-label={`Remove ${displayUser(user)}`}
                          >
                            x
                          </button>
                        )}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm font-medium text-[#A3AED0]">
                      No users assigned
                    </span>
                  )}
                </div>
                {!readOnly && (
                  optionList.length ? (
                    <PayoutDropdown
                      value="Add user"
                      options={optionList
                        .filter((option) => !level.users.includes(option.id))
                        .map((option) => option.label)}
                      onChange={(label) => {
                        const userId = idByLabel.get(label);
                        if (userId) addUser(index, userId);
                      }}
                      className="mt-3 min-w-0"
                      buttonClassName="h-9"
                    />
                  ) : (
                    <p className="mt-3 text-xs font-medium text-[#505E8E]">
                      {apiMode
                        ? "Add users under Payouts → Users first."
                        : "No users available."}
                    </p>
                  )
                )}
              </div>
              )}
            </div>

            {readOnly ? (
              <div>
                <FieldLabel>{level.stage === "M" ? "Role" : "Approval Mode"}</FieldLabel>
                <div className="flex min-h-[44px] items-center rounded-[6px] border border-[#E0E5F2] bg-[#F4F7FE] px-3 text-sm font-semibold text-[#1B163B]">
                  {level.stage === "M" ? "Creates transfer request" : approvalModeLabel(level)}
                </div>
              </div>
            ) : level.stage === "M" ? (
              <div>
                <FieldLabel>Role</FieldLabel>
                <div className="flex min-h-[44px] items-center rounded-[6px] border border-[#E0E5F2] bg-[#F4F7FE] px-3 text-sm font-semibold text-[#1B163B]">
                  Creates transfer request
                </div>
                <p className="mt-2 text-xs font-medium text-[#505E8E]">
                  Members can initiate payouts for this school when assigned to a Maker group.
                </p>
              </div>
            ) : (
              <div>
                <PayoutDropdown
                  label="Approval Mode"
                  value={level.mode}
                  options={["Any One Approves", "All Must Approve"]}
                  onChange={(mode) =>
                    updateLevel(index, (current) => ({
                      ...current,
                      mode: mode as ApprovalGroupLevel["mode"],
                      minApprovals: undefined,
                    }))
                  }
                  className="min-w-0"
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const legacyApprovalGroups = initialApprovalGroups.map((group) => [
  group.name,
  group.description,
  group.levels,
  group.flow,
  group.users,
  group.createdAt,
  group.createdBy,
  group.lastUpdated,
  group.status,
  group.tone,
]);

function WorkflowPreview({
  steps,
}: {
  steps?: Array<{ stage: string; title: string; users: string; helper: string }>;
}) {
  const resolved =
    steps?.length
      ? steps
      : [
          {
            stage: "M",
            title: "Maker",
            users: "-",
            helper: "Creates payout request",
          },
        ];

  return (
    <div className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
      <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
        Workflow Preview
      </h2>
      {resolved.map((step, index) => (
        <div key={`${step.title}-${index}`} className="relative flex gap-5 pb-8">
          {index < resolved.length - 1 && (
            <span className="absolute left-5 top-11 h-10 w-px bg-[#D6DCEB]" />
          )}
          <span
            className={
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold " +
              (stageClassName[step.stage] || stageClassName.C)
            }
          >
            {step.stage === "✓" ? "\u2713" : <LuUsers />}
          </span>
          <div>
            <p className="text-sm font-semibold text-[#1B163B]">{step.title}</p>
            {step.users && (
              <p className="mt-2 text-xs font-medium text-[#505E8E]">{step.users}</p>
            )}
            <p className="mt-2 text-xs font-medium text-[#505E8E]">{step.helper}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateApprovalGroup({
  onSave,
  memberUserOptions = [],
}: {
  onSave: (group: ApprovalGroup, stepUpToken?: string) => Promise<void> | void;
  memberUserOptions?: MemberUserOption[];
}) {
  const navigate = useNavigate();
  const apiMode = isPayoutApiMode();
  const stepUpRequired = isPayoutStepUpEnabled();
  const [groupName, setGroupName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [status, setStatus] = React.useState<ApprovalGroupStatus>("Active");
  const [levelDetails, setLevelDetails] = React.useState<ApprovalGroupLevel[]>(
    [
      {
        stage: "M",
        title: "Maker",
        users: [],
        mode: "Create Only",
      },
      {
        stage: "C",
        title: "Checker",
        users: [],
        mode: "Any One Approves",
      },
      {
        stage: "A",
        title: "Approver",
        users: [],
        mode: "All Must Approve",
      },
    ],
  );
  const stageTitle = (levelDetails[0]?.title || "Maker") as
    | "Maker"
    | "Checker"
    | "Approver"
    | "Auditor";
  const approvalMode = levelDetails[0]?.mode || "Any One Approves";
  const memberIds = levelDetails.flatMap((level) => level.users);
  const stagesWithMembers = levelDetails.filter((level) => level.users.length > 0);
  const [message, setMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [verificationMethod, setVerificationMethod] = React.useState<
    "authenticator" | "sms"
  >("authenticator");
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill(""),
  );
  const submitInFlightRef = React.useRef(false);

  const apiStageCode =
    stageTitle === "Maker"
      ? "M"
      : stageTitle === "Approver"
        ? "A"
        : stageTitle === "Auditor"
          ? "U"
          : "C";
  const apiStage =
    stageTitle === "Maker"
      ? "MAKER"
      : stageTitle === "Approver"
        ? "APPROVER"
        : stageTitle === "Auditor"
          ? "AUDITOR"
          : "CHECKER";
  const singleLevel = levelDetails[0] || {
    stage: "C" as const,
    title: "Checker",
    users: [] as string[],
    mode: "Any One Approves" as ApprovalGroupLevel["mode"],
  };
  const activeLevels = levelDetails;
  const approvalStageLevels = levelDetails.filter((level) => level.stage !== "M");
  const assignedApprovalMemberCount = levelDetails.reduce(
    (count, level) => count + level.users.length,
    0,
  );
  const labelById = React.useMemo(() => {
    const map = new Map<string, string>();
    memberUserOptions.forEach((option) => map.set(option.id, option.label));
    return map;
  }, [memberUserOptions]);

  const buildGroupsForApi = (): ApprovalGroup[] | null => {
    if (!groupName.trim()) {
      setMessage("Please enter a group name before saving.");
      return null;
    }
    const filledLevels = levelDetails.filter((level) => level.users.length > 0);
    if (!filledLevels.length) {
      setMessage("Add at least one user to Maker, Checker, Approver, or Auditor.");
      return null;
    }
    if (filledLevels.some((level) => !level.users.some((user) => user.startsWith("usr_")))) {
      setMessage(
        memberUserOptions.length
          ? "Select enrolled payout users from the dropdown (usr_* IDs)."
          : "Add users under Payouts -> Users first.",
      );
      return null;
    }

    const baseName = groupName.trim();
    const useSuffix = filledLevels.length > 1;

    return filledLevels.map((level) => {
      const memberUserIds = level.users.filter((user) => user.startsWith("usr_"));
      const stageApi =
        level.stage === "M"
          ? "MAKER"
          : level.stage === "A"
            ? "APPROVER"
            : level.stage === "U"
              ? "AUDITOR"
              : "CHECKER";
      const stageName =
        level.title ||
        (stageApi === "MAKER"
          ? "Maker"
          : stageApi === "APPROVER"
            ? "Approver"
            : stageApi === "AUDITOR"
              ? "Auditor"
              : "Checker");
      const resolvedName = useSuffix ? `${baseName} - ${stageName}` : baseName;
      return {
        id: resolvedName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
        name: resolvedName,
        description: description.trim() || `${stageName} approval group for payout transfers`,
        levels: "1 Level",
        flow: stageName,
        users: summarizeApprovalGroupUsers([level]),
        memberUserIds,
        apiStage: stageApi,
        apiLogic: level.mode === "All Must Approve" ? "ALL_MUST" : "ANY_ONE",
        levelDetails: [level],
        createdAt: formatPayoutDateTime(new Date()),
        createdBy: "-",
        lastUpdated: formatPayoutDateTime(new Date()),
        status,
        tone: "bg-[#6687FF1A] text-[#6687FF]",
      };
    });
  };

  const buildGroup = (): ApprovalGroup | null => {
    if (!groupName.trim()) {
      setMessage("Please enter a group name before saving.");
      return null;
    }
    if (levelDetails.some((level) => !level.users.length)) {
      setMessage("Please assign at least one user to every approval level.");
      return null;
    }
    const minApprovalError = minimumApprovalValidationError(levelDetails);
    if (minApprovalError) {
      setMessage(minApprovalError);
      return null;
    }
    const memberUserIds = levelDetails
      .flatMap((level) => level.users)
      .filter((user) => user.startsWith("usr_"));
    const levels = levelDetails;
    const primaryStage = levels.find((level) => level.stage !== "M") || levels[0];
    const flow = levels.map((level) => level.title).join(" -> ");
    return {
      id: groupName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, ""),
      name: groupName.trim(),
      description: description.trim() || "Approval group for payout transfers",
      levels: `${levels.length} ${levels.length === 1 ? "Level" : "Levels"}`,
      flow,
      users: summarizeApprovalGroupUsers(levels),
      memberUserIds: memberUserIds.length ? memberUserIds : undefined,
      apiStage:
        primaryStage?.stage === "M"
          ? "MAKER"
          : primaryStage?.stage === "A"
            ? "APPROVER"
            : primaryStage?.stage === "U"
              ? "AUDITOR"
              : "CHECKER",
      apiLogic:
        primaryStage?.mode === "All Must Approve"
          ? "ALL_MUST"
          : primaryStage?.mode === "Minimum N Approvals"
            ? "MIN_N"
            : "ANY_ONE",
      levelDetails: levels,
      createdAt: formatPayoutDateTime(new Date()),
      createdBy: "-",
      lastUpdated: formatPayoutDateTime(new Date()),
      status,
      tone: "bg-[#6687FF1A] text-[#6687FF]",
    };
  };

  const persistGroup = async (label: string) => {
    if (submitInFlightRef.current || isSubmitting) return;
    const nextGroup = buildGroup();
    if (!nextGroup) return;
    submitInFlightRef.current = true;
    try {
      setIsSubmitting(true);
      const stepUpToken =
        apiMode && isPayoutStepUpEnabled()
          ? await verifyStepUp({
              purpose: "CREATE_APPROVAL_GROUP",
              method: verificationMethod,
              code: verificationCode.join("") || "000000",
              subjectId: nextGroup.name,
            })
          : undefined;
      if (!apiMode) persistApprovalGroupLevelCache(nextGroup);
      await onSave(nextGroup, stepUpToken);
      setMessage(`${label} saved for ${groupName}.`);
      window.setTimeout(() => navigate("/payouts/approval-rules/approval-groups"), 700);
    } catch (error) {
      setMessage(errorMessage(error, "Unable to save this approval group."));
      setIsVerifying(false);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSave = async (label: string) => {
    if (apiMode && stepUpRequired && !isVerifying) {
      if (!buildGroup()) return;
      setMessage("");
      setVerificationCode(Array(6).fill(""));
      setIsVerifying(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (apiMode && stepUpRequired && isVerifying) {
      if (verificationCode.join("").length !== 6) {
        setMessage("Enter the 6-digit verification code before creating this group.");
        return;
      }
    }
    await persistGroup(label);
  };

  const previewSteps = apiMode
    ? levelDetails.map((level) => ({
          stage: level.stage,
          title: level.title,
          users:
            level.users.map((id) => labelById.get(id) || id).join(", ") || "No members yet",
          helper:
            level.stage === "M" ? "Creates payout request" : approvalModeLabel(level),
        }))
    : [
        {
          stage: "M",
          title: "Maker",
          users: "Maker group members",
          helper: "Creates payout request",
        },
        {
          stage: apiStageCode,
          title: stageTitle,
          users: memberIds.map((id) => labelById.get(id) || id).join(", ") || "No members yet",
          helper: approvalMode,
        },
        {
          stage: "✓",
          title: "Ready for workflows",
          users: "",
          helper: "Attach this group to a matching workflow stage",
        },
      ];

  if (apiMode && isVerifying) {
    return (
      <div className="mx-auto max-w-[920px] space-y-5">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-8 new-shadow">
          <h2 className="text-[22px] font-semibold text-[#1B163B]">
            Verify Approval Group Create
          </h2>
          <p className="mt-2 text-sm font-medium text-[#505E8E]">
            Confirm creating {groupName} ({stageTitle}) with a verification code.
          </p>
          <div className="mt-6 flex gap-3">
            {(["authenticator"] as const).map((method) => (
              <button
                key={method}
                type="button"
                onClick={() => setVerificationMethod("authenticator")}
                className={
                  "h-10 rounded-[6px] px-4 text-sm font-semibold " +
                  (verificationMethod === method
                    ? "bg-[#1B163B] text-white"
                    : "border border-[#E0E5F2] bg-white text-[#1B163B]")
                }
              >
                Authenticator
              </button>
            ))}
          </div>
          <div className="mt-6 flex gap-2">
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                value={digit}
                maxLength={1}
                onChange={(event) => {
                  const value = event.target.value.replace(/\D/g, "").slice(-1);
                  setVerificationCode((current) => {
                    const next = [...current];
                    next[index] = value;
                    return next;
                  });
                }}
                className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] text-center text-lg font-semibold text-[#1B163B]"
              />
            ))}
          </div>
          {message && (
            <p className="mt-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
              {message}
            </p>
          )}
          <div className="mt-8 flex justify-between">
            <button
              type="button"
              onClick={() => {
                setIsVerifying(false);
                setMessage("");
              }}
              className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
            >
              Back
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => handleSave("Approval group")}
              className="h-10 rounded-[6px] bg-edviron_black px-8 text-sm font-semibold text-white disabled:opacity-60"
            >
              {isSubmitting ? "Creating..." : "Verify & Create"}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const nextLevelTitle = nextMissingApprovalLevelTitle(levelDetails);

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_330px]">
      <div className="space-y-5">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-center gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DBEAFE] text-sm font-semibold text-[#6687FF]">
              1
            </span>
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Group Information
            </h2>
          </div>
          <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_260px]">
            <TextInput
              label="Group Name"
              placeholder="Enter group name"
              required
              value={groupName}
              onChange={setGroupName}
            />
            <ApprovalGroupStatusToggle value={status} onChange={setStatus} />
          </div>
          <label className="mt-6 block">
            <FieldLabel>Description</FieldLabel>
            <textarea
              className="h-[104px] w-full resize-none rounded-[6px] border border-[#E0E5F2] bg-white px-3 py-3 text-sm font-medium text-[#1B163B] outline-none"
              value={description}
              maxLength={250}
              onChange={(event) => setDescription(event.target.value)}
            />
            <p className="-mt-6 mr-4 text-right text-xs font-medium text-[#505E8E]">
              {description.length}/250
            </p>
          </label>
          {message && (
            <p className="mt-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
              {message}
            </p>
          )}
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-center gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DBEAFE] text-sm font-semibold text-[#6687FF]">
              2
            </span>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                {apiMode ? "Stage & Members" : "Approval Levels"}
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                Maker is required. Checker, Approver, and Auditor stages can be removed and added back.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <ApprovalGroupLevelEditor
              levels={levelDetails}
              onChange={setLevelDetails}
              memberUserOptions={memberUserOptions}
            />
            <button
              type="button"
              disabled={!nextLevelTitle}
              onClick={() => {
                if (!nextLevelTitle) return;
                setLevelDetails((current) =>
                  sortApprovalLevels([
                    ...current,
                    createApprovalLevel(nextLevelTitle, apiMode),
                  ]),
                );
              }}
              className="mt-6 flex h-[58px] w-full flex-col items-center justify-center rounded-[8px] border border-dashed border-[#6687FF] text-sm font-semibold text-[#6687FF] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex items-center gap-2">
                <FiPlus />
                {nextLevelTitle
                  ? `Add ${nextLevelTitle} ${apiMode ? "Stage" : "Level"}`
                  : apiMode
                    ? "All Stages Added"
                    : "All Levels Added"}
              </span>
              <span className="mt-1 text-xs font-medium text-[#505E8E]">
                {apiMode
                  ? "Removed Checker, Approver, and Auditor stages can be added back here."
                  : "Removed Checker, Approver, and Auditor levels can be added back here."}
              </span>
            </button>
          </div>
        </section>

        <div className="flex items-center justify-between rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <Link
            to="/payouts/approval-rules/approval-groups"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={() => handleSave("Approval group")}
            disabled={isSubmitting}
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="h-10 rounded-[6px] px-8 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting
              ? "Saving..."
              : apiMode && stepUpRequired
                ? "Continue"
                : apiMode
                  ? "Save Approval Groups"
                  : "Create Group"}
          </button>
        </div>
      </div>

      <aside className="space-y-5">
        <WorkflowPreview steps={apiMode ? previewSteps : undefined} />
        <div className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <h2 className="mb-5 text-[18px] font-semibold text-[#1B163B]">
            Summary
          </h2>
          {[
            ["Flow", activeLevels.map((level) => level.title).join(" -> ") || "-"],
            ["Approval Stages", String(approvalStageLevels.length)],
            ["Members", String(assignedApprovalMemberCount)],
          ].map(([label, value]) => (
            <div
              key={label}
              className="flex items-center justify-between border-b border-[#E0E5F2] py-4 text-sm"
            >
              <span className="font-medium text-[#505E8E]">{label}</span>
              <span className="font-semibold text-[#1B163B]">{value}</span>
            </div>
          ))}
          <div className="py-4">
            <p className="mb-3 text-sm font-semibold text-[#1B163B]">
              Approval Logic
            </p>
            <div className="rounded-[6px] border border-[#6687FF] bg-[#6687FF1A] px-3 py-3 text-sm font-semibold text-[#6687FF]">
              {displayValue(
                levelDetails
                  .map((level) =>
                    level.mode && level.mode !== "Create Only"
                      ? `${level.title} (${approvalModeLabel(level)})`
                      : level.title,
                  )
                  .join(" -> "),
              )}
            </div>
          </div>
          <div className="border-t border-[#E0E5F2] pt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-[#505E8E]">Used in Workflows</span>
              <span className="font-semibold text-[#1B163B]">-</span>
            </div>
            <p className="mt-4 text-xs font-medium text-[#505E8E]">
              This will be available after the group is created.
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}

function ViewApprovalGroupDetails({ groups }: { groups: ApprovalGroup[] }) {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const decodedGroupId = groupId ? decodeURIComponent(groupId) : "";
  const group =
    groups.find((item) => item.id === decodedGroupId) ||
    groups.find((item) => item.name === decodedGroupId);

  if (!group) {
    return (
      <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <h1 className="text-[22px] font-semibold text-[#1B163B]">
          Approval group not found
        </h1>
        <p className="mt-2 text-sm font-medium text-[#505E8E]">
          The selected approval group was not returned by the backend.
        </p>
        <button
          type="button"
          onClick={() => navigate("/payouts/approval-rules/approval-groups")}
          className="mt-5 flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
        >
          <FiArrowLeft />
          Back to Groups
        </button>
      </section>
    );
  }

  const groupLevels = getApprovalGroupLevels(group);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-4 flex items-center gap-3">
            <h1 className="text-[24px] font-semibold leading-8 text-[#1B163B]">
              {group.name}
            </h1>
            <span
              className={
                "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                (group.status === "Active"
                  ? "bg-[#DCFCE7] text-[#16A34A]"
                  : "bg-[#E5E7EB] text-[#505E8E]")
              }
            >
              {group.status}
            </span>
          </div>
          <p className="text-sm font-medium text-[#505E8E]">
            {group.description}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={getApprovalGroupEditPath(group.id)}
            className="flex h-10 items-center gap-2 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white"
          >
            <FiEdit2 />
            Edit Group
          </Link>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        {[
          ["Group ID", group.id.toUpperCase(), <LuCalendar />],
          ["Created By", group.createdBy, <LuUserCog />],
          ["Created On", group.createdAt, <LuCalendar />],
          ["Last Updated", group.lastUpdated, <FiClock />],
        ].map(([label, value, icon]) => (
          <div
            key={String(label)}
            className="flex min-h-[88px] items-center gap-5 rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-4 new-shadow"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#F4F7FE] text-lg text-[#6687FF]">
              {icon as React.ReactNode}
            </span>
            <span>
              <span className="block text-xs font-semibold text-[#505E8E]">
                {String(label)}
              </span>
              <span className="mt-2 block text-sm font-semibold text-[#1B163B]">
                {String(value)}
              </span>
            </span>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_330px]">
        <div className="space-y-5">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <div className="mb-6 flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DBEAFE] text-sm font-semibold text-[#6687FF]">
                1
              </span>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Group Information
              </h2>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              {[
                ["Group Name", group.name],
                ["Status", group.status],
                ["Approval Flow", group.flow],
                ["Description", group.description],
              ].map(([label, value]) => (
                <div key={label} className={label === "Description" ? "lg:col-span-2" : ""}>
                  <FieldLabel>{label}</FieldLabel>
                  <div className="min-h-[44px] rounded-[6px] border border-[#E0E5F2] bg-[#F4F7FE] px-3 py-3 text-sm font-semibold text-[#1B163B]">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
            <div className="mb-6 flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DBEAFE] text-sm font-semibold text-[#6687FF]">
                2
              </span>
              <div>
                <h2 className="text-[18px] font-semibold text-[#1B163B]">
                  Approval Levels
                </h2>
                <p className="mt-1 text-sm font-medium text-[#505E8E]">
                  Users assigned to each approval level in this group.
                </p>
              </div>
            </div>
            <ApprovalGroupLevelEditor levels={groupLevels} readOnly />
          </section>
        </div>

        <aside className="space-y-5">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Group Summary
            </h2>
            {[
              ["Levels", group.levels],
              ["Approval Flow", group.flow],
              ["Users", groupLevels.flatMap((level) => level.users).join(", ")],
              ["Status", group.status],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex items-center justify-between border-b border-[#E0E5F2] py-4 text-sm last:border-b-0"
              >
                <span className="font-medium text-[#505E8E]">{label}</span>
                <span className="max-w-[180px] text-right font-semibold text-[#1B163B]">
                  {value}
                </span>
              </div>
            ))}
          </section>
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Used In
            </h2>
            <p className="mt-3 text-sm font-medium text-[#505E8E]">
              This group is available for workflows once linked by the backend.
            </p>
            <div className="mt-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
              -
            </div>
          </section>
        </aside>
      </div>

      <div className="flex items-center justify-between">
        <Link
          to="/payouts/approval-rules/approval-groups"
          className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
        >
          <FiArrowLeft />
          Back to Groups
        </Link>
      </div>
    </div>
  );
}

type EditApprovalGroupStep = "details" | "verify" | "confirm";

function EditApprovalGroup({
  groups,
  onSave,
  onStepChange,
  memberUserOptions = [],
}: {
  groups: ApprovalGroup[];
  onSave: (group: ApprovalGroup, stepUpToken?: string) => Promise<void> | void;
  onStepChange?: (step: EditApprovalGroupStep) => void;
  memberUserOptions?: MemberUserOption[];
}) {
  const { groupId } = useParams();
  const decodedGroupId = groupId ? decodeURIComponent(groupId) : "";
  const group =
    groups.find((item) => item.id === decodedGroupId) ||
    groups.find((item) => item.name === decodedGroupId);
  const navigate = useNavigate();

  if (!group) {
    return (
      <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <h1 className="text-[22px] font-semibold text-[#1B163B]">
          Approval group not found
        </h1>
        <p className="mt-2 text-sm font-medium text-[#505E8E]">
          The selected approval group was not returned by the backend.
        </p>
        <button
          type="button"
          onClick={() => navigate("/payouts/approval-rules/approval-groups")}
          className="mt-5 flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
        >
          <FiArrowLeft />
          Back to Groups
        </button>
      </section>
    );
  }

  return (
    <EditApprovalGroupForm
      group={group}
      onSave={onSave}
      onStepChange={onStepChange}
      memberUserOptions={memberUserOptions}
    />
  );
}

function EditApprovalGroupForm({
  group,
  onSave,
  onStepChange,
  memberUserOptions = [],
}: {
  group: ApprovalGroup;
  onSave: (group: ApprovalGroup, stepUpToken?: string) => Promise<void> | void;
  onStepChange?: (step: EditApprovalGroupStep) => void;
  memberUserOptions?: MemberUserOption[];
}) {
  const [groupName, setGroupName] = React.useState(group.name);
  const [description, setDescription] = React.useState(group.description);
  const [flow, setFlow] = React.useState(group.flow);
  const [levelDetails, setLevelDetails] = React.useState<ApprovalGroupLevel[]>(
    getApprovalGroupLevels(group),
  );
  const apiMode = isPayoutApiMode();
  const stageTitle = (levelDetails[0]?.title || "Checker") as
    | "Maker"
    | "Checker"
    | "Approver"
    | "Auditor";
  const [status, setStatus] = React.useState<ApprovalGroupStatus>(group.status);
  const [message, setMessage] = React.useState("");
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [verificationMethod, setVerificationMethod] = React.useState<
    "authenticator" | "sms"
  >("authenticator");
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill(""),
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    setFlow(levelDetails.map((level) => level.title).join(" -> "));
  }, [levelDetails]);

  React.useEffect(() => {
    onStepChange?.(isSuccess ? "confirm" : isVerifying ? "verify" : "details");
  }, [isSuccess, isVerifying, onStepChange]);

  const normalizedOriginalLevels = JSON.stringify(getApprovalGroupLevels(group));
  const normalizedCurrentLevels = JSON.stringify(levelDetails);
  const hasChanges =
    groupName.trim() !== group.name ||
    description.trim() !== group.description ||
    flow !== group.flow ||
    status !== group.status ||
    normalizedCurrentLevels !== normalizedOriginalLevels;

  const EditGroupSteps = ({ active }: { active: "details" | "verify" | "confirm" }) => {
    const steps = [
      { key: "details", label: "Group Details" },
      { key: "verify", label: "Verification" },
      { key: "confirm", label: "Confirm" },
    ] as const;
    const activeIndex = steps.findIndex((step) => step.key === active);

    return (
      <div className="mb-5 grid rounded-[8px] border border-[#E0E5F2] bg-white new-shadow md:grid-cols-3">
        {steps.map((step, index) => {
          const completed = index < activeIndex;
          const isActive = step.key === active;
          return (
            <button
              key={step.key}
              type="button"
              onClick={() => {
                if (step.key === "details" && !isSuccess) {
                  setIsVerifying(false);
                  setMessage("");
                }
              }}
              disabled={step.key !== "details" || isSuccess}
              className={
                "flex h-14 items-center gap-3 px-5 text-left text-sm font-semibold " +
                (isActive
                  ? "border-b-2 border-[#1B163B] text-[#1B163B]"
                  : "text-[#505E8E]") +
                (step.key === "details" && !isSuccess ? "" : " cursor-default")
              }
            >
              <span
                className={
                  "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold " +
                  (isActive
                    ? "bg-[#1B163B] text-white"
                    : completed
                    ? "bg-[#EAF2EE] text-[#2B6B47]"
                    : "bg-[#F4F7FE] text-[#505E8E]")
                }
              >
                {completed ? "\u2713" : index + 1}
              </span>
              {step.label}
            </button>
          );
        })}
      </div>
    );
  };

  const handleCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setVerificationCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
  };

  const saveGroup = async () => {
    const primaryStage = apiMode
      ? levelDetails[0]
      : levelDetails.find((level) => level.stage !== "M") || levelDetails[0];
    const nextGroup: ApprovalGroup = {
      ...group,
      name: groupName.trim(),
      description: description.trim() || group.description,
      flow,
      levels: `${levelDetails.length} ${levelDetails.length === 1 ? "Level" : "Levels"}`,
      users: summarizeApprovalGroupUsers(levelDetails),
      levelDetails,
      memberUserIds: levelDetails
        .flatMap((level) => level.users)
        .filter((user) => user.startsWith("usr_")),
      apiStage:
        primaryStage?.stage === "M"
          ? "MAKER"
          : primaryStage?.stage === "A"
            ? "APPROVER"
            : primaryStage?.stage === "U"
              ? "AUDITOR"
              : "CHECKER",
      apiLogic:
        primaryStage?.mode === "All Must Approve"
          ? "ALL_MUST"
          : "ANY_ONE",
      status,
      lastUpdated: formatPayoutDateTime(new Date()),
    };
    try {
      setIsSubmitting(true);
      const stepUpToken = isPayoutApiMode()
        ? await verifyStepUp({
            purpose: "UPDATE_APPROVAL_GROUP",
            method: verificationMethod,
            code: verificationCode.join(""),
            subjectId: group.id,
          })
        : undefined;
      if (!isPayoutApiMode()) persistApprovalGroupLevelCache(nextGroup);
      await onSave(nextGroup, stepUpToken);
      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setMessage(errorMessage(error, "Unable to update this approval group."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = async () => {
    if (!isVerifying) {
      if (!groupName.trim()) {
        setMessage("Please enter a group name before continuing.");
        return;
      }
      if (levelDetails.some((level) => !level.users.length)) {
        setMessage("Please assign at least one user to every approval level.");
        return;
      }
      const minApprovalError = minimumApprovalValidationError(levelDetails);
      if (minApprovalError) {
        setMessage(minApprovalError);
        return;
      }
      if (!hasChanges) {
        setMessage("Make at least one change before continuing to verification.");
        return;
      }
      setMessage("");
      setVerificationCode(Array(6).fill(""));
      setIsVerifying(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (verificationCode.join("").length !== 6) {
      setMessage("Enter the 6-digit verification code before updating this group.");
      return;
    }

    await saveGroup();
  };

  if (isSuccess) {
    return (
      <div>
        <EditGroupSteps active="confirm" />
        <div className="mx-auto max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-8 text-center new-shadow">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF2EE] text-2xl text-[#2B6B47]">
            <LuShield />
          </div>
          <h1 className="mt-5 text-[24px] font-semibold text-[#1B163B]">
            Approval Group Updated
          </h1>
          <p className="mt-2 text-sm font-medium text-[#505E8E]">
            {groupName} has been updated after verification.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/payouts/approval-rules/approval-groups"
              className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
            >
              Back to Groups
            </Link>
            <Link
              to={getApprovalGroupDetailsPath(group.id)}
              className="flex h-10 items-center rounded-[6px] bg-edviron_black px-6 text-sm font-semibold text-white"
            >
              View Details
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isVerifying) {
    return (
      <div>
        <EditGroupSteps active="verify" />
        <section className="mx-auto max-w-[920px] rounded-[8px] border border-[#E0E5F2] bg-white p-8 new-shadow">
          <h2 className="text-[22px] font-semibold text-[#1B163B]">
            Verify Approval Group Update
          </h2>
          <p className="mt-2 text-sm font-medium text-[#505E8E]">
            Enter the Google Authenticator code before saving changes.
          </p>
          <div className="mt-6">
            {[
              ["authenticator", "Google Authenticator", "Use the code from your authenticator app"],
            ].map(([method, title, helper]) => (
              <button
                key={method}
                type="button"
                onClick={() => setVerificationMethod("authenticator")}
                className={
                  "flex min-h-[92px] items-center justify-between rounded-[8px] border px-5 text-left " +
                  (verificationMethod === method
                    ? "border-[#1B163B] bg-white"
                    : "border-[#E0E5F2] bg-white")
                }
              >
                <span>
                  <span className="block font-semibold text-[#1B163B]">{title}</span>
                  <span className="mt-1 block text-sm font-medium text-[#505E8E]">
                    {helper}
                  </span>
                </span>
                {verificationMethod === method && (
                  <span className="text-[#2B6B47]">{"\u2713"}</span>
                )}
              </button>
            ))}
          </div>
          <div className="mt-7">
            <FieldLabel>Verification Code</FieldLabel>
            <div className="flex flex-wrap gap-3">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  value={digit}
                  onChange={(event) => handleCodeChange(index, event.target.value)}
                  className="h-12 w-12 rounded-[6px] border border-[#E0E5F2] bg-white text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                  maxLength={1}
                  inputMode="numeric"
                />
              ))}
            </div>
            {message && (
              <p className="mt-4 rounded-[6px] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
                {message}
              </p>
            )}
          </div>
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={handleContinue}
              disabled={isSubmitting}
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="h-10 rounded-[6px] px-7 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Saving..." : "Verify & Save"}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const totalAssignedUsers = new Set(levelDetails.flatMap((level) => level.users)).size;
  const nextLevelTitle = nextMissingApprovalLevelTitle(levelDetails);

  return (
    <>
      <EditGroupSteps active="details" />
      <form
        id="edit-approval-group-form"
        onSubmit={(event) => {
          event.preventDefault();
          handleContinue();
        }}
        className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_330px]"
      >
        <div className="space-y-5">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-center gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DBEAFE] text-sm font-semibold text-[#6687FF]">
              1
            </span>
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Group Information
            </h2>
          </div>
          <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_260px]">
            <TextInput
              label="Group Name"
              value={groupName}
              onChange={setGroupName}
              placeholder="Enter group name"
              required
            />
            <ApprovalGroupStatusToggle value={status} onChange={setStatus} />
          </div>
          <label className="mt-6 block">
            <FieldLabel>Description</FieldLabel>
            <textarea
              value={description}
              maxLength={250}
              onChange={(event) => setDescription(event.target.value)}
              className="h-[104px] w-full resize-none rounded-[6px] border border-[#E0E5F2] bg-white px-3 py-3 text-sm font-medium text-[#1B163B] outline-none"
            />
            <p className="-mt-6 mr-4 text-right text-xs font-medium text-[#505E8E]">
              {description.length}/250
            </p>
          </label>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-6 flex items-center gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DBEAFE] text-sm font-semibold text-[#6687FF]">
              2
            </span>
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                {apiMode ? "Stage & Members" : "Approval Levels"}
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {apiMode
                  ? "Each group serves one stage. Any Level 2–4 user may be assigned; Level 1 (Observer) is excluded."
                  : "Change assigned users and approval modes for this group."}
              </p>
            </div>
          </div>
          <ApprovalGroupLevelEditor
            levels={levelDetails}
            onChange={setLevelDetails}
            memberUserOptions={memberUserOptions}
          />
          {!apiMode && (
            <button
              type="button"
              disabled={!nextLevelTitle}
              onClick={() => {
                if (!nextLevelTitle) return;
                setLevelDetails((current) =>
                  sortApprovalLevels([
                    ...current,
                    createApprovalLevel(nextLevelTitle, false),
                  ]),
                );
              }}
              className="mt-6 flex h-[58px] w-full flex-col items-center justify-center rounded-[8px] border border-dashed border-[#6687FF] text-sm font-semibold text-[#6687FF] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className="flex items-center gap-2">
                <FiPlus />
                {nextLevelTitle ? `Add ${nextLevelTitle} Stage` : "All Stages Added"}
              </span>
              <span className="mt-1 text-xs font-medium text-[#505E8E]">
                Removed stages can be added back here.
              </span>
            </button>
          )}
          {apiMode && (
            <p className="mt-4 text-sm font-medium text-[#505E8E]">
              Each saved group maps to one stage ({stageTitle}). Level 2–4 users may be assigned; Level 1 (Observer) is excluded.
            </p>
          )}
        </section>

        {message && (
          <p className="rounded-[6px] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
            {message}
          </p>
        )}

        <div className="flex items-center justify-between rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <Link
            to={getApprovalGroupDetailsPath(group.id)}
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </Link>
          <button
            type="submit"
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="h-10 rounded-[6px] px-8 text-sm font-semibold text-white"
          >
            Continue to Verification
          </button>
        </div>
      </div>

      <aside className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <h2 className="text-[18px] font-semibold text-[#1B163B]">
          Group Summary
        </h2>
        {[
          ["Group Name", groupName || "-"],
          ["Approval Flow", flow],
          ["Status", status],
          ["Total Users", String(totalAssignedUsers)],
          ...levelDetails.map((level) => [
            level.title,
            `${level.users.length} ${level.users.length === 1 ? "User" : "Users"}`,
          ]),
        ].map(([label, value]) => (
          <div
            key={label}
            className="flex items-center justify-between border-b border-[#E0E5F2] py-4 text-sm last:border-b-0"
          >
            <span className="font-medium text-[#505E8E]">{label}</span>
            <span className="max-w-[160px] text-right font-semibold text-[#1B163B]">
              {value}
            </span>
          </div>
        ))}
      </aside>
      </form>
    </>
  );
}

function ApprovalGroups({
  groups,
  onGroupsChange,
}: {
  groups: ApprovalGroup[];
  onGroupsChange: (updater: (current: ApprovalGroup[]) => ApprovalGroup[]) => void;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = React.useState("");
  const [searchField, setSearchField] = React.useState("Group Name");
  const [statusFilter, setStatusFilter] = React.useState("All Status");
  const [pageSize, setPageSize] = React.useState("10 per page");
  const [openMenu, setOpenMenu] = React.useState<FloatingActionMenuState>(null);
  const [message, setMessage] = React.useState("");
  const [deleteGroup, setDeleteGroup] = React.useState<ApprovalGroup | null>(null);

  const filteredGroups = groups.filter((group) => {
    const query = search.trim().toLowerCase();
    const getGroupSearchValue = (field: string) => {
      switch (field) {
        case "Description":
          return group.description;
        case "Flow":
          return group.flow;
        case "Users":
          return group.users.join(" ");
        case "Status":
          return group.status;
        case "Group Name":
        default:
          return group.name;
      }
    };
    const matchesSearch =
      !query || getGroupSearchValue(searchField).toLowerCase().includes(query);
    const matchesStatus =
      statusFilter === "All Status" || group.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleGroupAction = (group: ApprovalGroup, action: string) => {
    setOpenMenu(null);
    if (action === "view") {
      navigate(getApprovalGroupDetailsPath(group.id));
      return;
    }
    if (action === "edit") {
      navigate(getApprovalGroupEditPath(group.id));
      return;
    }
    if (action === "toggle") {
      onGroupsChange((current) =>
        current.map((item) =>
          item.id === group.id
            ? { ...item, status: item.status === "Active" ? "Inactive" : "Active" }
            : item,
        ),
      );
      setMessage(`${group.name} status updated.`);
      return;
    }
    if (action === "delete") {
      setDeleteGroup(group);
    }
  };

  const approvalGroupRows = filteredGroups.map((group) => [
      <div className="flex items-center gap-4">
        <span
          className={
            "flex h-9 w-9 items-center justify-center rounded-[6px] text-base " +
            group.tone
          }
        >
          <LuUsers />
        </span>
        <PayoutTableCell
          value={displayValue(group.name)}
          valueClassName="font-semibold text-[#1B163B]"
        />
      </div>,
      <PayoutTableCell
        value={displayValue(group.description)}
        valueClassName="text-[#505E8E]"
        maxWidthClassName="max-w-[260px]"
      />,
      <span>
        <PayoutTableCell
          value={displayValue(group.levels)}
          valueClassName="font-semibold text-[#1B163B]"
        />
        <span
          className="mt-1 block max-w-[220px] truncate text-[#505E8E]"
          title={String(displayValue(group.flow))}
        >
          {displayValue(group.flow)}
        </span>
      </span>,
      <ApprovalGroupUsersCell group={group} />,
      <PayoutTableCell
        value={displayValue(group.createdAt)}
        valueClassName="font-semibold text-[#1B163B]"
      />,
      <PayoutTableCell
        value={displayValue(group.createdBy)}
        valueClassName="font-semibold text-[#1B163B]"
      />,
      <PayoutTableCell
        value={displayValue(group.lastUpdated)}
        valueClassName="font-semibold text-[#1B163B]"
      />,
      <span
        className={
          "rounded-[5px] px-2 py-1 text-xs font-semibold " +
          (group.status === "Active"
            ? "bg-[#DCFCE7] text-[#16A34A]"
            : "bg-[#E5E7EB] text-[#505E8E]")
        }
      >
        {displayValue(group.status)}
      </span>,
      <FloatingActionMenuButton
        id={group.id}
        label={group.name}
        openMenu={openMenu}
        setOpenMenu={setOpenMenu}
        onAction={(action) => handleGroupAction(group, action)}
      />,
    ]);

  const uniqueGroupUsers = new Set(
    groups.flatMap((group) => [
      ...(group.memberUserIds || []),
      ...group.users.filter((user) => !user.startsWith("+")),
    ]),
  );
  const latestGroupUpdate =
    groups
      .map((group) => group.lastUpdated)
      .filter((value) => value && value !== "-")
      .sort((a, b) => Date.parse(b) - Date.parse(a))[0] || "-";

  return (
    <>
      <ApprovalTabs />
      <div>
        <div>
          <div className="mb-6 grid gap-5 lg:grid-cols-4">
            {[
              ["Total Groups", String(groups.length), "Approval groups", <LuUsers />],
              [
                "Groups in Use",
                String(groups.filter((group) => group.status === "Active").length),
                "Active groups",
                <LuShield />,
              ],
              ["Total Users", String(uniqueGroupUsers.size), "Across all groups", <LuList />],
              ["Last Updated", latestGroupUpdate, "-", <LuCalendar />],
            ].map(([label, value, helper, icon], index) => (
              <div
                key={String(label)}
                className="flex min-h-[112px] items-center gap-5 rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-5 new-shadow"
              >
                <div
                  className={
                    "payout-kpi-icon flex h-11 w-11 shrink-0 items-center justify-center text-xl " +
                    (index === 2
                      ? "bg-[#F59E0B1A] text-[#F97316]"
                      : index === 3
                        ? "bg-[#DBEAFE] text-[#0284C7]"
                        : "bg-[#6687FF1A] text-[#6687FF]")
                  }
                >
                  {icon as React.ReactNode}
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#505E8E]">
                    {String(label)}
                  </p>
                  <p className="mt-2 text-[24px] font-semibold leading-8 text-[#1B163B]">
                    {String(value)}
                  </p>
                  <p className="mt-2 text-xs font-medium text-[#505E8E]">
                    {String(helper)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <section className="payout-filter-card rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <div className="mb-5 grid items-end gap-3 md:grid-cols-[minmax(360px,470px)_minmax(260px,1fr)_60px]">
              <PayoutSearchFilter
                value={search}
                onChange={setSearch}
                filterBy={searchField}
                onFilterByChange={setSearchField}
                filterOptions={["Group Name", "Description", "Flow", "Users", "Status"]}
                placeholder={`Search by ${searchField}`}
                className="w-full"
              />
              <PayoutDropdown
                label="Status"
                value={statusFilter}
                options={["All Status", "Active", "Inactive"]}
                onChange={setStatusFilter}
                className="w-full min-w-0"
              />
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setSearchField("Group Name");
                  setStatusFilter("All Status");
                  setMessage("Approval group filters cleared.");
                }}
                className="payout-filter-button h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
              >
                Clear
              </button>
            </div>
            {message && (
              <p className="mb-4 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
                {message}
              </p>
            )}
            <PayoutDataTable
              columns={[
                "Group Name",
                "Description",
                "Levels",
                "Users",
                "Created At",
                "Created By",
                "Last Updated",
                "Status",
                "Actions",
              ]}
              rows={approvalGroupRows}
              minWidth="min-w-[1320px]"
              footer={
                <div className="flex min-h-[52px] w-full items-center justify-between gap-4 bg-white px-5 py-4 text-xs font-medium text-[#505E8E]">
                  <span className="min-w-max">
                    Showing {filteredGroups.length ? 1 : 0} to{" "}
                    {filteredGroups.length} of {filteredGroups.length} groups
                  </span>
                  <div className="ml-auto flex min-w-max items-center gap-4">
                    <PayoutDropdown
                      value={pageSize}
                      options={["5 per page", "10 per page", "20 per page"]}
                      onChange={setPageSize}
                      className="min-w-[120px]"
                      buttonClassName="h-9"
                    />
                    <button className="h-9 w-9 rounded-md border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]">
                      1
                    </button>
                  </div>
                </div>
              }
            />
            {deleteGroup && (
              <DeleteVerificationModal
                itemName={deleteGroup.name}
                itemType="approval group"
                onCancel={() => setDeleteGroup(null)}
                onDelete={() => {
                  onGroupsChange((current) =>
                    current.filter((item) => item.id !== deleteGroup.id),
                  );
                  setMessage(`${deleteGroup.name} deleted after verification.`);
                }}
              />
            )}
            <div className="hidden overflow-x-auto">
              <table className="w-full min-w-[1240px] table-auto">
                <thead className="bg-[#A9B2CF1A] text-center">
                  <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                    <th className="px-4 py-3.5 text-center">Group Name</th>
                    <th className="px-4 py-3.5 text-center">Description</th>
                    <th className="px-4 py-3.5 text-center">Levels</th>
                    <th className="px-4 py-3.5 text-center">Users</th>
                    <th className="px-4 py-3.5 text-center">Created At</th>
                    <th className="px-4 py-3.5 text-center">Created By</th>
                    <th className="px-4 py-3.5 text-center">Last Updated</th>
                    <th className="px-4 py-3.5 text-center">Status</th>
                    <th className="px-4 py-3.5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {legacyApprovalGroups.map(([name, desc, levels, flow, users, createdAt, createdBy, lastUpdated, status, tone]) => (
                    <tr
                      key={String(name)}
                      className="border-t border-[#E0E5F2] text-xs font-medium text-[#1B163B]"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-4">
                          <span
                            className={
                              "flex h-9 w-9 items-center justify-center rounded-[6px] text-base " +
                              String(tone)
                            }
                          >
                            <LuUsers />
                          </span>
                          <span className="font-semibold">{String(name)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[#505E8E]">
                        {String(desc)}
                      </td>
                      <td className="px-4 py-4">
                        <p className="font-semibold">{String(levels)}</p>
                        <p className="mt-1 text-[#505E8E]">{String(flow)}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex -space-x-2">
                          {(users as string[]).map((user) => (
                            <span
                              key={user}
                              className="flex h-7 w-7 items-center justify-center rounded-full border border-white bg-[#EDE9FE] text-[10px] font-semibold text-[#6687FF]"
                            >
                              {user}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 font-semibold">
                        {String(createdAt)}
                      </td>
                      <td className="px-4 py-4 font-semibold">
                        {String(createdBy)}
                      </td>
                      <td className="px-4 py-4 font-semibold">
                        {String(lastUpdated)}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={
                            "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                            (status === "Active"
                              ? "bg-[#DCFCE7] text-[#16A34A]"
                              : "bg-[#E5E7EB] text-[#505E8E]")
                          }
                        >
                          {String(status)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center">
                          <button>
                            <FiMoreVertical className="text-lg" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="mt-6 flex items-center gap-3 rounded-[6px] bg-[#6687FF1A] px-4 py-4 text-sm font-medium text-[#6687FF]">
            <FiInfo />
            Use approval groups in workflows to automatically apply the
            right approval path based on conditions.
          </div>
        </div>

      </div>
    </>
  );
}

type WorkflowStage = {
  id: string;
  code: "M" | "C" | "A" | "U";
  title: string;
  helper: string;
  users: string;
  role: string;
};

function NewWorkflow() {
  const navigate = useNavigate();
  const [workflowName, setWorkflowName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [appliesTo, setAppliesTo] = React.useState("Select transaction type");
  const [workflowType, setWorkflowType] = React.useState("Select workflow type");
  const [status, setStatus] = React.useState("Active");
  const [message, setMessage] = React.useState("");
  const [advancedOpen, setAdvancedOpen] = React.useState(true);
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [verificationMethod, setVerificationMethod] = React.useState<
    "authenticator" | "sms"
  >("authenticator");
  const [verificationCode, setVerificationCode] = React.useState<string[]>(
    Array(6).fill(""),
  );
  const [advanced, setAdvanced] = React.useState({
    amountRouting: false,
    holidayHandling: false,
    workflowExpiry: false,
  });
  const [stages, setStages] = React.useState<WorkflowStage[]>([
    {
      id: "maker",
      code: "M",
      title: "Maker",
      helper: "Initiates the request",
      users: "-",
      role: "Maker",
    },
    {
      id: "checker",
      code: "C",
      title: "Checker",
      helper: "Verifies and validates the request",
      users: "-",
      role: "Checker",
    },
    {
      id: "approver",
      code: "A",
      title: "Approver",
      helper: "Approves or rejects the request",
      users: "-",
      role: "Approver",
    },
  ]);

  const updateStage = (id: string, key: "users" | "role", value: string) => {
    setStages((current) =>
      current.map((stage) =>
        stage.id === id ? { ...stage, [key]: value } : stage,
      ),
    );
  };

  const addStage = () => {
    if (stages.length >= 5) {
      setMessage("A workflow can have up to 5 stages.");
      return;
    }
    setStages((current) => [
      ...current,
      {
        id: `auditor-${current.length + 1}`,
        code: "U",
        title: "Auditor",
        helper: "Reviews exception and compliance checks",
        users: "-",
        role: "Auditor",
      },
    ]);
  };

  const validateWorkflow = () => {
    if (!workflowName.trim()) {
      setMessage("Please enter a workflow name.");
      return false;
    }
    if (appliesTo === "Select transaction type") {
      setMessage("Please select where this workflow applies.");
      return false;
    }
    if (workflowType === "Select workflow type") {
      setMessage("Please select a workflow type.");
      return false;
    }
    return true;
  };

  const handleVerificationCodeChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setVerificationCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
  };

  const handleVerificationKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !verificationCode[index] && index > 0) {
      const previous = document.getElementById(
        `new-workflow-otp-${index - 1}`,
      ) as HTMLInputElement | null;
      previous?.focus();
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isVerifying) {
      if (!validateWorkflow()) return;
      setMessage("");
      setVerificationCode(Array(6).fill(""));
      setIsVerifying(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (verificationCode.join("").length !== 6) {
      setMessage("Enter the 6-digit verification code before saving this workflow.");
      return;
    }

    setMessage(`${workflowName} saved successfully.`);
    window.setTimeout(() => navigate("/payouts/approval-rules"), 700);
  };

  if (isVerifying) {
    const verificationDescription =
      "Enter the 6-digit code from your Google Authenticator app.";

    return (
      <form
        id="new-workflow-form"
        onSubmit={handleSubmit}
        className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_390px]"
      >
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          {message && (
            <p className="mb-5 rounded-[6px] border border-[#F59E0B66] bg-[#FEF3C7] px-4 py-3 text-sm font-semibold text-[#92400E]">
              {message}
            </p>
          )}

          <div className="mb-6 flex items-start gap-5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#F4F7FE] text-xl text-[#1B163B]">
              <LuShield />
            </span>
            <div>
              <h2 className="text-[22px] font-semibold text-[#1B163B]">
                Verify Workflow Save
              </h2>
              <p className="mt-2 text-sm font-medium text-[#505E8E]">
                {verificationDescription}
              </p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {[
              {
                key: "authenticator",
                title: "Google Authenticator",
                helper: "Recommended for approval workflow changes",
                icon: <LuShield />,
              },
            ].map((method) => {
              const active = verificationMethod === method.key;
              return (
                <button
                  key={method.key}
                  type="button"
                  onClick={() => setVerificationMethod("authenticator")}
                  className={
                    "flex min-h-[92px] items-center justify-between rounded-[8px] border px-5 text-left " +
                    (active
                      ? "border-[#1B163B] bg-white"
                      : "border-[#E0E5F2] bg-white hover:bg-[#F8FAFD]")
                  }
                >
                  <span className="flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-[8px] bg-[#F4F7FE] text-lg text-[#1B163B]">
                      {method.icon}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-[#1B163B]">
                        {method.title}
                      </span>
                      <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                        {method.helper}
                      </span>
                    </span>
                  </span>
                  {active && (
                    <span className="text-xs font-semibold text-[#166534]">
                      Selected
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-8">
            <FieldLabel required>Verification Code</FieldLabel>
            <div className="flex flex-wrap gap-3">
              {verificationCode.map((digit, index) => (
                <input
                  key={index}
                  id={`new-workflow-otp-${index}`}
                  value={digit}
                  inputMode="numeric"
                  maxLength={1}
                  onChange={(event) => {
                    handleVerificationCodeChange(index, event.target.value);
                    if (event.target.value && index < 5) {
                      const next = document.getElementById(
                        `new-workflow-otp-${index + 1}`,
                      ) as HTMLInputElement | null;
                      next?.focus();
                    }
                  }}
                  onKeyDown={(event) => handleVerificationKeyDown(index, event)}
                  className="h-14 w-14 rounded-[8px] border border-[#E0E5F2] bg-white text-center text-xl font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
                />
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-5">
              <button
                type="button"
                onClick={() => setVerificationCode(Array(6).fill(""))}
                className="text-sm font-semibold text-[#1B163B]"
              >
                Resend code
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-[#E0E5F2] pt-5">
            <button
              type="button"
              onClick={() => {
                setIsVerifying(false);
                setMessage("");
              }}
              className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft />
              Back to Edit
            </button>
            <button
              type="submit"
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="flex h-10 min-w-[180px] items-center justify-center gap-3 rounded-[6px] px-6 text-sm font-semibold text-white shadow-sm"
            >
              Verify & Continue
              <FaArrowRight className="text-xs" />
            </button>
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Verification Summary
          </h2>
          <div className="mt-5 border-t border-[#E0E5F2] pt-5">
            {[
              ["Workflow Name", workflowName],
              ["Applies To", appliesTo],
              ["Workflow Type", workflowType],
              ["Status", status],
              [
                "Verification",
                "Google Authenticator",
              ],
            ].map(([label, value]) => (
              <div key={label} className="mb-4 flex justify-between gap-4 text-sm">
                <span className="font-medium text-[#505E8E]">{label}</span>
                <span className="text-right font-semibold text-[#1B163B]">
                  {value}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-medium text-[#505E8E]">
            This verification protects approval workflow changes before they are
            applied.
          </div>
        </aside>
      </form>
    );
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_390px]">
      <form id="new-workflow-form" onSubmit={handleSubmit} className="space-y-5">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="mb-6 text-[18px] font-semibold text-[#1B163B]">
            1. Workflow Details
          </h2>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.4fr)]">
            <TextInput
              label="Workflow Name"
              placeholder="Enter workflow name"
              required
              value={workflowName}
              onChange={setWorkflowName}
            />
            <label className="block">
              <FieldLabel>Description</FieldLabel>
              <textarea
                value={description}
                maxLength={200}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Enter workflow description (optional)"
                className="h-[92px] w-full resize-none rounded-[6px] border border-[#E0E5F2] bg-white px-3 py-3 text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
              />
              <p className="-mt-6 mr-4 text-right text-xs font-medium text-[#505E8E]">
                {description.length} / 200
              </p>
            </label>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <div>
              <PayoutDropdown
                label="Applies To"
                value={appliesTo}
                options={[
                  "Select transaction type",
                  "Payouts",
                  "Transfers",
                  "Internal Transfers",
                  "Recharges",
                  "All Transactions",
                ]}
                onChange={setAppliesTo}
                className="min-w-0"
                buttonClassName="h-11"
              />
              <p className="mt-2 text-xs font-medium text-[#505E8E]">
                Choose the transaction(s) this workflow will apply to
              </p>
            </div>
            <PayoutDropdown
              label="Workflow Type"
              value={workflowType}
              options={[
                "Select workflow type",
                "Maker -> Checker -> Approver",
                "Maker -> Approver",
                "Maker -> Checker",
                "Custom Workflow",
              ]}
              onChange={setWorkflowType}
              className="min-w-0"
              buttonClassName="h-11"
            />
            <PayoutDropdown
              label="Status"
              value={status}
              options={["Active", "Inactive"]}
              onChange={setStatus}
              className="min-w-0"
              buttonClassName="h-11"
            />
          </div>
          {message && (
            <p className="mt-5 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#1B163B]">
              {message}
            </p>
          )}
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <div className="mb-5 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                2. Define Approval Stages
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                Add and configure stages in the order they should be executed
              </p>
            </div>
            <button
              type="button"
              onClick={addStage}
              className="flex h-10 items-center gap-2 rounded-[6px] bg-[#F4F7FE] px-5 text-sm font-semibold text-[#1B163B]"
            >
              <FiPlus />
              Add Stage
            </button>
          </div>

          <div className="overflow-hidden rounded-[8px] border border-[#E0E5F2]">
            {stages.map((stage, index) => (
              <div
                key={stage.id}
                className="grid min-h-[64px] grid-cols-[28px_1fr_150px_150px_44px] items-center gap-4 border-b border-[#E0E5F2] px-4 last:border-b-0"
              >
                <button
                  type="button"
                  className="text-lg text-[#A3AED0]"
                  title="Stage is ordered by sequence"
                >
                  {">"}
                </button>
                <div className="flex items-center gap-4">
                  <span
                    className={
                      "flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold " +
                      stageClassName[stage.code]
                    }
                  >
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-[#1B163B]">
                      {stage.title}
                    </p>
                    <p className="mt-1 text-xs font-medium text-[#505E8E]">
                      {stage.helper}
                    </p>
                  </div>
                </div>
                <PayoutDropdown
                  value={stage.users}
                  options={["-", "1 User", "2 Users", "3 Users", "4 Users", "Approval Group"]}
                  onChange={(value) => updateStage(stage.id, "users", value)}
                  className="min-w-0"
                  buttonClassName="h-9 bg-[#F4F7FE] border-transparent text-xs"
                />
                <PayoutDropdown
                  value={stage.role}
                  options={["Maker", "Checker", "Approver", "Auditor"]}
                  onChange={(value) => updateStage(stage.id, "role", value)}
                  className="min-w-0"
                  buttonClassName="h-9 bg-[#F4F7FE] border-transparent text-xs"
                />
                <button
                  type="button"
                  onClick={() =>
                    setStages((current) =>
                      current.length <= 1
                        ? current
                        : current.filter((item) => item.id !== stage.id),
                    )
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-[6px] text-[#505E8E] hover:bg-[#F4F7FE]"
                >
                  <FiMoreVertical />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-[6px] border border-[#6687FF33] bg-[#6687FF1A] px-4 py-4 text-sm font-medium text-[#505E8E]">
            <div className="mb-2 flex items-center gap-3 font-semibold text-[#6687FF]">
              <FiInfo />
              Stages will be executed in the order shown above.
            </div>
            You can add up to 5 stages in a workflow.
          </div>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <button
            type="button"
            onClick={() => setAdvancedOpen((current) => !current)}
            className="flex w-full items-center justify-between text-left"
          >
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              3. Advanced Settings (Optional)
            </h2>
            <FiChevronDown className={advancedOpen ? "rotate-180" : ""} />
          </button>
          {advancedOpen && (
            <div className="mt-6 grid gap-5 lg:grid-cols-3">
              {[
                [
                  "amountRouting",
                  "Enable amount based routing",
                  "Use different approval flows based on transaction amount",
                ],
                [
                  "holidayHandling",
                  "Enable holiday/after hours handling",
                  "Define how requests are handled on non-working days or after hours",
                ],
                [
                  "workflowExpiry",
                  "Add workflow expiry (SLA)",
                  "Set time limit for completion of each stage",
                ],
              ].map(([key, label, helper]) => (
                <label key={key} className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={advanced[key as keyof typeof advanced]}
                    onChange={(event) =>
                      setAdvanced((current) => ({
                        ...current,
                        [key]: event.target.checked,
                      }))
                    }
                    className="mt-1 h-4 w-4 rounded border-[#A3AED0]"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-[#1B163B]">
                      {label}
                    </span>
                    <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                      {helper}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </section>
      </form>

      <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <h2 className="text-[18px] font-semibold text-[#1B163B]">
          Workflow Summary
        </h2>
        <div className="mt-5 border-t border-[#E0E5F2] pt-5">
          <h3 className="text-sm font-semibold text-[#1B163B]">
            Basic Information
          </h3>
          {[
            ["Workflow Name", workflowName || "-"],
            ["Applies To", appliesTo === "Select transaction type" ? "-" : appliesTo],
            ["Workflow Type", workflowType === "Select workflow type" ? "-" : workflowType],
          ].map(([label, value]) => (
            <div key={label} className="mt-4 flex justify-between gap-4 text-sm">
              <span className="font-medium text-[#505E8E]">{label}</span>
              <span className="text-right font-semibold text-[#1B163B]">
                {value}
              </span>
            </div>
          ))}
          <div className="mt-4 flex justify-between gap-4 text-sm">
            <span className="font-medium text-[#505E8E]">Status</span>
            <span
              className={
                "rounded-full px-3 py-1 text-xs font-semibold " +
                (status === "Active"
                  ? "bg-[#DCFCE7] text-[#16A34A]"
                  : "bg-[#E5E7EB] text-[#505E8E]")
              }
            >
              {status}
            </span>
          </div>
        </div>

        <div className="mt-6 border-t border-[#E0E5F2] pt-5">
          <h3 className="text-sm font-semibold text-[#1B163B]">
            Approval Stages
          </h3>
          {stages.map((stage, index) => (
            <div key={stage.id} className="relative mt-5 flex gap-4">
              {index < stages.length - 1 && (
                <span className="absolute left-4 top-9 h-10 w-px bg-[#D6DCEB]" />
              )}
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F4F7FE] text-sm font-semibold text-[#1B163B]">
                {index + 1}
              </span>
              <div>
                <p className="text-sm font-semibold text-[#1B163B]">
                  {stage.title}
                </p>
                <p className="mt-1 text-xs font-medium text-[#505E8E]">
                  {stage.users}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-[6px] border border-[#6687FF33] bg-[#6687FF1A] px-4 py-4 text-sm font-medium text-[#505E8E]">
          <div className="mb-2 flex items-center gap-2 font-semibold text-[#1B163B]">
            <FiInfo className="text-[#6687FF]" />
            How it works
          </div>
          Requests will flow through the stages in the order defined. Each stage
          must be completed before moving to the next.
        </div>
      </aside>
    </div>
  );
}

function PayoutApprovalRules() {
  const location = useLocation();
  const isOverview = location.pathname === "/payouts/approval-rules";
  const isNewRule = location.pathname.endsWith("/workflow-rules/new");
  const isEditRule =
    location.pathname.includes("/payouts/approval-rules/workflow-rules/") &&
    location.pathname.endsWith("/edit");
  const isNewWorkflow = location.pathname.endsWith("/workflows/new");
  const isGroups = location.pathname.endsWith("/approval-groups");
  const isConfigureMatrix = location.pathname.endsWith("/rule-matrix/configure");
  const isNewGroup = location.pathname.endsWith("/approval-groups/new");
  const isEditGroup =
    location.pathname.includes("/payouts/approval-rules/approval-groups/") &&
    location.pathname.endsWith("/edit");
  const isGroupDetails =
    location.pathname.includes("/payouts/approval-rules/approval-groups/") &&
    !isNewGroup &&
    !isEditGroup;
  const isRuleDetails =
    location.pathname.includes("/payouts/approval-rules/workflow-rules/") &&
    !isNewRule &&
    !isEditRule;
  const editRuleName = isEditRule
    ? decodeURIComponent(
        location.pathname.split("/workflow-rules/")[1]?.replace("/edit", "") ?? "",
      )
    : "";
  const [approvalRules, setApprovalRules] = React.useState<ApprovalRule[]>(() =>
    isPayoutApiMode() ? [] : hydrateApprovalRules(),
  );
  const [approvalGroups, setApprovalGroups] = React.useState<ApprovalGroup[]>(() =>
    isPayoutApiMode() ? [] : hydrateApprovalGroups(),
  );
  const [ruleMatrixRows, setRuleMatrixRows] = React.useState<RuleMatrixRow[]>(() =>
    isPayoutApiMode() ? [] : hydrateRuleMatrixRows(),
  );
  const [workflowFormStep, setWorkflowFormStep] =
    React.useState<WorkflowFormStep>("details");
  const [editGroupStep, setEditGroupStep] =
    React.useState<EditApprovalGroupStep>("details");
  const approvalRulesResource = usePayoutResource<ApprovalRule[]>(
    [] as ApprovalRule[],
    (signal) =>
      payoutApi.approvalWorkflows({}, signal).then((records) =>
        sanitizeApprovalRules(
          records.map(
            (record, index) =>
              approvalRuleFromApiRecord(record, index, getRulePresentation(index)) as ApprovalRule,
          ),
        ),
      ),
    [],
  );
  const approvalGroupsResource = usePayoutResource<ApprovalGroup[]>(
    [] as ApprovalGroup[],
    (signal) =>
      payoutApi.approvalGroups({}, signal).then((records) =>
        sanitizeApprovalGroups(
          dedupeApprovalGroups(
            records.map((record, index) =>
              approvalGroupFromApiRecord(record, index, {
                tone: getRulePresentation(index).tone,
              }) as ApprovalGroup,
            ),
          ),
        ),
      ),
    [],
  );
  const ruleMatrixResource = usePayoutResource<RuleMatrixRow[]>(
    [] as RuleMatrixRow[],
    (signal) =>
      payoutApi
        .approvalRulesMatrix(signal)
        .then((record) => sanitizeRuleMatrixRows(ruleMatrixRowsFromApiRecord(record))),
    [],
  );
  const approvalSummaryResource = usePayoutResource<Record<string, unknown> | null>(
    null,
    (signal) => payoutApi.approvalSummary(signal),
  );
  const payoutUsersResource = usePayoutResource<MemberUserOption[]>(
    [],
    (signal) =>
      payoutApi.users({}, signal).then((records) =>
        records
          .map((row) => {
            const user = userFromApiRecord(row);
            const id = user.id;
            if (!id || !String(id).startsWith("usr_")) return null;
            const email = user.email || "";
            const label = user.name
              ? `${user.name}${email ? ` (${email})` : ""}`
              : email || id;
            return { id, label };
          })
          .filter((row): row is MemberUserOption => Boolean(row)),
      ),
    [],
  );
  const memberUserOptions = React.useMemo(
    () => payoutUsersResource.data,
    [payoutUsersResource.data],
  );
  const displayedWorkflowStats = React.useMemo(() => {
    if (approvalSummaryResource.data) {
      return workflowStatsFromSummary(approvalSummaryResource.data, workflowStats);
    }
    return workflowStats.map((stat) => ({
      ...stat,
      value: "0",
      helper: "From payouts-service",
    }));
  }, [approvalSummaryResource.data]);
  const editingRule = isEditRule
    ? findRuleByRouteKey(approvalRules, editRuleName)
    : null;
  const editRuleDisplayName = editingRule?.name ?? editRuleName;

  React.useEffect(() => {
    purgeSeedApprovalRulesStorage();
  }, []);

  React.useEffect(() => {
    setApprovalRules(approvalRulesResource.data);
    if (!isPayoutApiMode() && approvalRulesResource.source !== "api") {
      persistApprovalRules(approvalRulesResource.data);
    }
  }, [approvalRulesResource.data, approvalRulesResource.source]);

  React.useEffect(() => {
    setApprovalGroups(approvalGroupsResource.data);
    if (!isPayoutApiMode() && approvalGroupsResource.source !== "api") {
      persistApprovalGroups(approvalGroupsResource.data);
    }
  }, [approvalGroupsResource.data, approvalGroupsResource.source]);

  React.useEffect(() => {
    setRuleMatrixRows(ruleMatrixResource.data);
    if (!isPayoutApiMode() && ruleMatrixResource.source !== "api") {
      persistRuleMatrixRows(ruleMatrixResource.data);
    }
  }, [ruleMatrixResource.data, ruleMatrixResource.source]);

  React.useEffect(() => {
    if (!isNewRule && !isEditRule) {
      setWorkflowFormStep("details");
    }
  }, [isNewRule, isEditRule, location.pathname]);

  React.useEffect(() => {
    if (!isEditGroup) {
      setEditGroupStep("details");
    }
  }, [isEditGroup, location.pathname]);

  const updateApprovalRules = (
    updater: (current: ApprovalRule[]) => ApprovalRule[],
    options: { syncDeletes?: boolean } = {},
  ) => {
    setApprovalRules((current) => {
      const nextRules = updater(current);
      if (!isPayoutApiMode()) {
        persistApprovalRules(nextRules);
      }
      if (isPayoutApiMode() && options.syncDeletes !== false) {
        current
          .filter(
            (rule) =>
              !nextRules.some(
                (nextRule) => getRuleRouteKey(nextRule) === getRuleRouteKey(rule),
              ),
          )
          .forEach((rule) => {
            payoutApi
              .deleteApprovalWorkflow(
                getRuleRouteKey(rule),
                createPayoutIdempotencyKey(
                  "approval-workflow-delete",
                  getRuleRouteKey(rule),
                ),
              )
              .catch(() => undefined);
          });
      }
      return nextRules;
    });
  };

  const handleSaveRule = async (
    rule: ApprovalRule,
    previousRouteKey?: string,
    stepUpToken?: string,
  ) => {
    if (isPayoutApiMode()) {
      const existingRule = previousRouteKey
        ? findRuleByRouteKey(approvalRules, previousRouteKey)
        : undefined;
      const workflowId = existingRule?.workflowId || previousRouteKey;
      const idempotencyKey = createPayoutIdempotencyKey(
        workflowId ? "approval-workflow-update" : "approval-workflow-create",
        `${workflowId || "new"}:${rule.name}`,
      );
      const request =
        workflowId && previousRouteKey
          ? payoutApi.updateApprovalWorkflow(
              workflowId,
              approvalRulePayload(rule),
              idempotencyKey,
              stepUpToken,
            )
          : payoutApi.createApprovalWorkflow(
              approvalRulePayload(rule),
              idempotencyKey,
              stepUpToken,
            );
      await request;
      approvalRulesResource.refresh();
      ruleMatrixResource.refresh();
      approvalSummaryResource.refresh();
      return;
    }
    updateApprovalRules((current) => [
      rule,
      ...current.filter(
        (item) =>
          getRuleRouteKey(item) !== getRuleRouteKey(rule) &&
          (!previousRouteKey || getRuleRouteKey(item) !== previousRouteKey),
      ),
    ], { syncDeletes: false });
  };

  const updateApprovalGroups = (
    updater: (current: ApprovalGroup[]) => ApprovalGroup[],
    options: { syncDeletes?: boolean } = {},
  ) => {
    setApprovalGroups((current) => {
      const nextGroups = updater(current);
      if (!isPayoutApiMode()) {
        persistApprovalGroups(nextGroups);
      }
      if (isPayoutApiMode() && options.syncDeletes !== false) {
        current
          .filter(
            (group) =>
              !nextGroups.some((nextGroup) => nextGroup.id === group.id),
          )
          .forEach((group) => {
            payoutApi
              .deleteApprovalGroup(
                group.id,
                createPayoutIdempotencyKey("approval-group-delete", group.id),
              )
              .catch(() => undefined);
          });
      }
      return nextGroups;
    });
  };

  const handleSaveGroup = async (group: ApprovalGroup, stepUpToken?: string) => {
    const isExistingGroup =
      group.id.startsWith("grp_") &&
      approvalGroups.some((item) => item.id === group.id);
    if (isPayoutApiMode()) {
      const idempotencyKey = createPayoutIdempotencyKey(
        isExistingGroup ? "approval-group-update" : "approval-group-create",
        group.id,
      );
      const request = isExistingGroup
        ? payoutApi.updateApprovalGroup(
            group.id,
            approvalGroupPayload(group),
            idempotencyKey,
            stepUpToken,
          )
        : payoutApi.createApprovalGroup(
            approvalGroupPayload(group),
            idempotencyKey,
            stepUpToken,
          );
      await request;
      approvalGroupsResource.refresh();
      approvalSummaryResource.refresh();
      return;
    }
    updateApprovalGroups((current) => [
      group,
      ...current.filter((item) => item.id !== group.id),
    ], { syncDeletes: false });
  };

  const handleSaveMatrixRows = async (
    rows: RuleMatrixRow[],
    stepUpToken?: string,
  ) => {
    if (isPayoutApiMode()) {
      await payoutApi.updateApprovalRulesMatrix(
        ruleMatrixPayload(rows),
        createPayoutIdempotencyKey("approval-rule-matrix-update", "matrix"),
        stepUpToken,
      );
      setRuleMatrixRows(rows);
      ruleMatrixResource.refresh();
      approvalRulesResource.refresh();
      approvalSummaryResource.refresh();
      return;
    }
    setRuleMatrixRows(rows);
    if (!isPayoutApiMode()) {
      persistRuleMatrixRows(rows);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      {!isRuleDetails && !isGroupDetails && (
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            {isEditRule
              ? `Edit ${editRuleDisplayName}`
              : isNewRule
              ? "New Workflow"
              : isNewWorkflow
              ? "New Workflow"
              : isConfigureMatrix
              ? "Configure Workflow Matrix"
              : isEditGroup
              ? "Edit Approval Group"
              : isNewGroup
                ? "Create Approval Group"
                : isGroups
                ? "Approval Groups"
                : isOverview
                  ? "Approval Workflows"
                  : "Approval Workflows"}
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            {isEditRule
              ? "Update this workflow and verify the changes before applying them"
              : isNewRule
              ? "Create a new workflow to define how transfers are approved in your organization"
              : isNewWorkflow
              ? "Create a new approval workflow to streamline payouts and transfers"
              : isConfigureMatrix
              ? "Set and edit the workflow matrix used as the source of truth for approval workflows"
              : isEditGroup
              ? "Update this approval group and verify the changes before applying them"
              : isNewGroup
                ? "Define approval levels, assign users and set approval logic for consistent and secure payouts."
                : isGroups
                ? "Create and manage approval groups to use in approval workflows."
                : isOverview
                  ? "Configure approval workflows for payouts and transfers"
                  : "Configure workflows that govern transfer approvals in your organization"}
          </p>
        </div>
        {(isNewRule || isEditRule) && workflowFormStep === "details" ? (
          <div className="flex items-center gap-4">
            <Link
              to="/payouts/approval-rules/workflow-rules"
              className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#6687FF]"
            >
              Cancel
            </Link>
            <button
              type="submit"
              form="new-approval-rule-form"
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="h-10 rounded-[6px] px-7 text-sm font-semibold text-white"
            >
              {isEditRule ? "Continue" : "Save Workflow"}
            </button>
          </div>
        ) : isNewRule || isEditRule ? null : isNewWorkflow ? (
          <div className="flex items-center gap-4">
            <Link
              to="/payouts/approval-rules"
              className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              Cancel
            </Link>
            <button
              type="submit"
              form="new-workflow-form"
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="flex h-10 items-center gap-3 rounded-[6px] px-7 text-sm font-semibold text-white"
            >
              Save & Next
              <FaArrowRight className="text-xs" />
            </button>
          </div>
        ) : isEditGroup && editGroupStep === "details" ? (
          <div className="flex items-center gap-4 pt-1">
            <Link
              to="/payouts/approval-rules/approval-groups"
              className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              Cancel
            </Link>
            <button
              type="submit"
              form="edit-approval-group-form"
              style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
              className="flex h-10 items-center gap-3 rounded-[6px] px-7 text-sm font-semibold text-white"
            >
              Continue
              <FaArrowRight className="text-xs" />
            </button>
          </div>
        ) : isEditGroup ? null : isNewGroup || isConfigureMatrix ? null : (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <PayoutDateRangeFilter />
            {isGroups ? (
              <Link
                to="/payouts/approval-rules/approval-groups/new"
                style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
                className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white shadow-sm"
              >
                <FiPlus className="text-lg" />
                New Group
              </Link>
            ) : isOverview && (
              <Link
                to="/payouts/approval-rules/workflow-rules/new"
                className="flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white"
              >
                <FiPlus className="text-lg" />
                New Workflow
              </Link>
            )}
          </div>
        )}
      </div>
      )}

      <Routes>
        <Route
          index
          element={
            <WorkflowOverview
              approvalRules={approvalRules}
              onRulesChange={updateApprovalRules}
              workflowStats={displayedWorkflowStats}
            />
          }
        />
        <Route
          path="workflows/new"
          element={<Navigate to="/payouts/approval-rules/workflow-rules/new" replace />}
        />
        <Route
          path="workflow-rules"
          element={
            <WorkflowRules
              approvalRules={approvalRules}
              onRulesChange={updateApprovalRules}
            />
          }
        />
        <Route
          path="workflow-rules/new"
          element={
            <NewRule
              approvalGroups={approvalGroups}
              onSave={handleSaveRule}
              onStepChange={setWorkflowFormStep}
            />
          }
        />
        <Route
          path="workflow-rules/:ruleName/edit"
          element={
            <EditRule
              approvalRules={approvalRules}
              approvalGroups={approvalGroups}
              onStepChange={setWorkflowFormStep}
              onSave={(rule, stepUpToken) =>
                handleSaveRule(rule, editRuleName, stepUpToken)
              }
            />
          }
        />
        <Route
          path="workflow-rules/:ruleName"
          element={
            <ViewRuleDetails
              approvalRules={approvalRules}
              approvalGroups={approvalGroups}
              onSave={handleSaveRule}
            />
          }
        />
        <Route
          path="approval-groups"
          element={
            <ApprovalGroups
              groups={approvalGroups}
              onGroupsChange={updateApprovalGroups}
            />
          }
        />
        <Route
          path="approval-groups/new"
          element={<CreateApprovalGroup onSave={handleSaveGroup} memberUserOptions={memberUserOptions} />}
        />
        <Route
          path="approval-groups/:groupId/edit"
          element={
            <EditApprovalGroup
              groups={approvalGroups}
              onSave={handleSaveGroup}
              onStepChange={setEditGroupStep}
              memberUserOptions={memberUserOptions}
            />
          }
        />
        <Route
          path="approval-groups/:groupId"
          element={<ViewApprovalGroupDetails groups={approvalGroups} />}
        />
        <Route
          path="rule-matrix/configure"
          element={
            <ConfigureRuleMatrix
              matrixItems={ruleMatrixRows}
              onSave={handleSaveMatrixRows}
            />
          }
        />
        <Route
          path="rule-matrix"
          element={<><ApprovalTabs /><RuleMatrix matrixItems={ruleMatrixRows} /></>}
        />
        <Route
          path="*"
          element={
            <WorkflowOverview
              approvalRules={approvalRules}
              onRulesChange={updateApprovalRules}
              workflowStats={displayedWorkflowStats}
            />
          }
        />
      </Routes>
    </div>
  );
}

export default PayoutApprovalRules;
